import { useFilePreview } from '@/contexts/file-preview'
import useUploadFile from '@/hooks/useUploadFile'
import CloseIcons from '@/icons/CloseIcons'
import CsvIcons from '@/icons/CsvIcons'
import ImageIcons from '@/icons/ImageIcons'
import MaximizeIcons from '@/icons/MaximizeIcons'
import MinimizeIcons from '@/icons/MinimizeIcons'
import PdfIcons from '@/icons/PdfIcons'
import RedoIcons from '@/icons/RedoIcons'
import TextIcons from '@/icons/TextIcons'
import UndoIcons from '@/icons/UndoIcons'
import { IconsProps } from '@/types/shared'
import { Node, mergeAttributes } from '@tiptap/core'
import Bold from '@tiptap/extension-bold'
import BulletList from '@tiptap/extension-bullet-list'
import Dropcursor from '@tiptap/extension-dropcursor'
import Italic from '@tiptap/extension-italic'
import Link from '@tiptap/extension-link'
import OrderedList from '@tiptap/extension-ordered-list'
import Paragraph from '@tiptap/extension-paragraph'
import Placeholder from '@tiptap/extension-placeholder'
import TextAlign from '@tiptap/extension-text-align'
import Underline from '@tiptap/extension-underline'
import {
    EditorContent,
    NodeViewWrapper,
    ReactNodeViewRenderer,
    useEditor,
} from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import React, { memo, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Tooltip, Typogaphy } from 'vnetwork-ui'

// Custom Image Node with delete functionality
const CustomImageComponent = ({ node, deleteNode, editor }: any) => {
    const { t } = useTranslation()
    const [showControls, setShowControls] = useState(false)
    const readOnly = editor?.storage?.customImage?.readOnly || false
    const isLoading = node.attrs.loading

    return (
        <NodeViewWrapper
            className="relative inline-block group"
            onMouseEnter={() => setShowControls(true)}
            onMouseLeave={() => setShowControls(false)}
        >
            {isLoading ? (
                <div
                    className="flex items-center justify-center bg-gray-100 rounded animate-pulse"
                    style={{
                        width: node.attrs.placeholderWidth || 200,
                        height: node.attrs.placeholderHeight || 150,
                    }}
                >
                    <div className="flex flex-col items-center gap-2 text-gray-400">
                        <svg
                            className="w-8 h-8 animate-spin"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                            />
                            <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                        </svg>
                        <span className="text-xs">Uploading...</span>
                    </div>
                </div>
            ) : (
                <img
                    {...node.attrs}
                    className="max-w-full h-auto rounded cursor-move"
                    draggable="true"
                />
            )}
            {showControls && !readOnly && !isLoading && (
                <div className="absolute top-2 right-2 flex gap-1">
                    <button
                        onClick={deleteNode}
                        className="bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600 transition-colors"
                        title={t('textEditor.deleteAttachment')}
                    >
                        ×
                    </button>
                </div>
            )}
        </NodeViewWrapper>
    )
}

const CustomImage = Node.create({
    name: 'customImage',
    group: 'block',
    atom: true,

    addAttributes() {
        return {
            src: {
                default: null,
            },
            alt: {
                default: null,
            },
            title: {
                default: null,
            },
            loading: {
                default: false,
            },
            placeholderWidth: {
                default: 200,
            },
            placeholderHeight: {
                default: 150,
            },
            uploadId: {
                default: null,
            },
        }
    },

    parseHTML() {
        return [
            {
                tag: 'img[src]',
            },
        ]
    },

    renderHTML({ HTMLAttributes }) {
        return ['img', mergeAttributes(HTMLAttributes)]
    },

    addNodeView() {
        return ReactNodeViewRenderer(CustomImageComponent, {
            as: 'div',
            contentDOMElementTag: 'div',
        })
    },

    addStorage() {
        return {
            readOnly: false,
        }
    },
})

// Template Variable Node - displays label but saves as ${name}
const TemplateVariableNode = Node.create({
    name: 'templateVariable',
    group: 'inline',
    inline: true,
    atom: true,

    addAttributes() {
        return {
            name: { default: '' },
            label: { default: '' },
            color: { default: 'blue' },
        }
    },

    parseHTML() {
        return [
            {
                tag: 'span[data-template-variable]',
                getAttrs: (dom) => {
                    const element = dom as HTMLElement
                    return {
                        name: element.getAttribute('data-name'),
                        label: element.getAttribute('data-label'),
                        color: element.getAttribute('data-color') || 'blue',
                    }
                },
            },
        ]
    },

    renderHTML({ HTMLAttributes }) {
        const colors = getVariableHexColors(HTMLAttributes.color)
        return [
            'span',
            mergeAttributes(HTMLAttributes, {
                'data-template-variable': '',
                'data-name': HTMLAttributes.name,
                'data-label': HTMLAttributes.label,
                'data-color': HTMLAttributes.color,
                class: 'template-variable-chip',
                style: `display: inline; background-color: ${colors.bg}; color: ${colors.text}; padding: 1px 5px; border-radius: 3px; font-size: 0.85em; cursor: default; user-select: none;`,
            }),
            HTMLAttributes.label || HTMLAttributes.name,
        ]
    },

    // Convert to ${name} when getting HTML for storage
    addStorage() {
        return {
            getPlainText: (node: any) => `\${${node.attrs.name}}`,
        }
    },
})

const BoldIcon: React.FC<IconsProps> = (props) => (
    <div {...props}>
        <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.6 10.79c.97-.67 1.65-1.77 1.65-2.79 0-2.26-1.75-4-4-4H7v14h7.04c2.09 0 3.71-1.7 3.71-3.79 0-1.52-.86-2.82-2.15-3.42zM10 6.5h3c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5h-3v-3zm3.5 9H10v-3h3.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5z" />
        </svg>
    </div>
)

const ItalicIcon: React.FC<IconsProps> = (props) => (
    <div {...props}>
        <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M10 4v3h2.21l-3.42 8H6v3h8v-3h-2.21l3.42-8H18V4z" />
        </svg>
    </div>
)

const UnderlineIcon: React.FC<IconsProps> = (props) => (
    <div {...props}>
        <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 17c3.31 0 6-2.69 6-6V3h-2.5v8c0 1.93-1.57 3.5-3.5 3.5S8.5 12.93 8.5 11V3H6v8c0 3.31 2.69 6 6 6zm-7 2v2h14v-2H5z" />
        </svg>
    </div>
)

const ListBulletIcon: React.FC<IconsProps> = (props) => (
    <div {...props}>
        <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M4 10.5c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zm0-6c-.83 0-1.5.67-1.5 1.5S3.17 7.5 4 7.5 5.5 6.83 5.5 6 4.83 4.5 4 4.5zm0 12c-.83 0-1.5.67-1.5 1.5s.67 1.5 1.5 1.5 1.5-.67 1.5-1.5-.67-1.5-1.5-1.5zM7 19h14v-2H7v2zm0-6h14v-2H7v2zm0-8v2h14V5H7z" />
        </svg>
    </div>
)

const ListNumberIcon: React.FC<IconsProps> = (props) => (
    <div {...props}>
        <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M2 17h2v.5H3v1h1v.5H2v1h3v-4H2v1zm1-9h1V4H2v1h1v3zm-1 3h1.8L2 13.1v.9h3v-1H3.2L5 10.9V10H2v1zm5-6v2h14V5H7zm0 14h14v-2H7v2zm0-6h14v-2H7v2z" />
        </svg>
    </div>
)

const AlignLeftIcon: React.FC<IconsProps> = (props) => (
    <div {...props}>
        <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M15 15H3v2h12v-2zm0-8H3v2h12V7zM3 13h18v-2H3v2zm0 8h18v-2H3v2zm0 8h18v-2H3v2zM3 3v2h18V3H3z" />
        </svg>
    </div>
)

const AlignCenterIcon: React.FC<IconsProps> = (props) => (
    <div {...props}>
        <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M7 15v2h10v-2H7zm-4 6h18v-2H3v2zm0-8h18v-2H3v2zm4-6v2h10V7H7zM3 3v2h18V3H3z" />
        </svg>
    </div>
)

const AlignRightIcon: React.FC<IconsProps> = (props) => (
    <div {...props}>
        <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 21h18v-2H3v2zm6-4h12v-2H9v2zm-6-4h18v-2H3v2zm6-4h12V7H9v2zM3 3v2h18V3H3z" />
        </svg>
    </div>
)

const AlignJustifyIcon: React.FC<IconsProps> = (props) => (
    <div {...props}>
        <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 21h18v-2H3v2zm0-4h18v-2H3v2zm0-4h18v-2H3v2zm0-4h18V7H3v2zm0-6v2h18V3H3z" />
        </svg>
    </div>
)

const ImageIcon: React.FC<IconsProps> = (props) => (
    <div {...props}>
        <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
        </svg>
    </div>
)

const AttachIcon: React.FC<IconsProps> = (props) => (
    <div {...props}>
        <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M2 12.5C2 9.46 4.46 7 7.5 7H18c2.21 0 4 1.79 4 4s-1.79 4-4 4H9.5a2.5 2.5 0 0 1 0-5H17v2H9.41c-.55 0-.55 1 0 1H18a2 2 0 0 0 0-4H7.5C5.57 9 4 10.57 4 12.5S5.57 16 7.5 16H17v2H7.5C4.46 18 2 15.54 2 12.5z" />
        </svg>
    </div>
)

const LinkIcon: React.FC<IconsProps> = (props) => (
    <div {...props}>
        <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z" />
        </svg>
    </div>
)

const PreviewIcon: React.FC<IconsProps> = (props) => (
    <div {...props}>
        <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
        </svg>
    </div>
)

const CodeBlockIcon: React.FC<IconsProps> = (props) => (
    <div {...props}>
        <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z" />
        </svg>
    </div>
)

const ImportHtmlIcon: React.FC<IconsProps> = (props) => (
    <div {...props}>
        <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M14 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zM6 20V4h7v5h5v11H6zm8-6.5l-3 3-1.41-1.41L11.17 14H8v-2h3.17l-1.59-1.59L11 9l3 3-2 2.5z" />
        </svg>
    </div>
)

const LineHeightIncreaseIcon: React.FC<IconsProps> = (props) => (
    <div {...props}>
        <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M9.17 8L12 5.17 14.83 8H13v3h-2V8H9.17zM21 3H3v2h18V3zm0 16H3v2h18v-2zm-18-6h18v-2H3v2zm6 3h2v3h1.83L10 22.83 7.17 19H9v-3z" />
        </svg>
    </div>
)

const LineHeightDecreaseIcon: React.FC<IconsProps> = (props) => (
    <div {...props}>
        <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M9.17 11H11V8h2v3h1.83L12 13.83 9.17 11zM21 3H3v2h18V3zm0 16H3v2h18v-2zm-18-6h18v-2H3v2zm6-3H7.17L10 7.17 12.83 10H11v3H9v-3z" />
        </svg>
    </div>
)

const VariableIcon: React.FC<IconsProps> = (props) => (
    <div {...props}>
        <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z" />
            <circle cx="12" cy="12" r="2" />
        </svg>
    </div>
)

interface EditorValue {
    text: string
    attachments: Array<{
        file: File
        url?: string // URL after upload
        uploading?: boolean
        progress?: number
    }>
}

interface TemplateVariable {
    name: string
    label: string
    description?: string
    color?:
        | 'blue'
        | 'green'
        | 'purple'
        | 'orange'
        | 'pink'
        | 'teal'
        | 'red'
        | 'yellow'
        | 'indigo'
        | 'cyan'
        | 'gray'
}

// Remove Vietnamese diacritics and normalize text
const removeVietnameseDiacritics = (str: string): string => {
    return str
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remove combining diacritical marks
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
}

// Generate variable name from label (remove diacritics, replace spaces with underscore)
const generateVariableName = (label: string): string => {
    return removeVietnameseDiacritics(label)
        ?.trim()
        ?.toLowerCase()
        ?.replace(/\s+/g, '_')
        ?.replace(/[^a-z0-9_]/g, '') // Only allow alphanumeric and underscore
}

// Color classes for variables
const VARIABLE_COLORS: Record<
    string,
    { bg: string; text: string; border: string; hover: string; dot: string }
> = {
    blue: {
        bg: 'bg-blue-50',
        text: 'text-blue-700',
        border: 'border-blue-200',
        hover: 'hover:bg-blue-100',
        dot: 'bg-blue-500',
    },
    green: {
        bg: 'bg-green-50',
        text: 'text-green-700',
        border: 'border-green-200',
        hover: 'hover:bg-green-100',
        dot: 'bg-green-500',
    },
    purple: {
        bg: 'bg-purple-50',
        text: 'text-purple-700',
        border: 'border-purple-200',
        hover: 'hover:bg-purple-100',
        dot: 'bg-purple-500',
    },
    orange: {
        bg: 'bg-orange-50',
        text: 'text-orange-700',
        border: 'border-orange-200',
        hover: 'hover:bg-orange-100',
        dot: 'bg-orange-500',
    },
    pink: {
        bg: 'bg-pink-50',
        text: 'text-pink-700',
        border: 'border-pink-200',
        hover: 'hover:bg-pink-100',
        dot: 'bg-pink-500',
    },
    teal: {
        bg: 'bg-teal-50',
        text: 'text-teal-700',
        border: 'border-teal-200',
        hover: 'hover:bg-teal-100',
        dot: 'bg-teal-500',
    },
    red: {
        bg: 'bg-red-50',
        text: 'text-red-700',
        border: 'border-red-200',
        hover: 'hover:bg-red-100',
        dot: 'bg-red-500',
    },
    yellow: {
        bg: 'bg-yellow-50',
        text: 'text-yellow-700',
        border: 'border-yellow-200',
        hover: 'hover:bg-yellow-100',
        dot: 'bg-yellow-500',
    },
    indigo: {
        bg: 'bg-indigo-50',
        text: 'text-indigo-700',
        border: 'border-indigo-200',
        hover: 'hover:bg-indigo-100',
        dot: 'bg-indigo-500',
    },
    cyan: {
        bg: 'bg-cyan-50',
        text: 'text-cyan-700',
        border: 'border-cyan-200',
        hover: 'hover:bg-cyan-100',
        dot: 'bg-cyan-500',
    },
    gray: {
        bg: 'bg-gray-100',
        text: 'text-gray-700',
        border: 'border-gray-300',
        hover: 'hover:bg-gray-200',
        dot: 'bg-gray-500',
    },
}

// Hex color values for inline styles (used in template variable nodes)
const VARIABLE_HEX_COLORS: Record<string, { bg: string; text: string }> = {
    blue: { bg: '#dbeafe', text: '#1d4ed8' },
    green: { bg: '#dcfce7', text: '#15803d' },
    purple: { bg: '#f3e8ff', text: '#7c3aed' },
    orange: { bg: '#ffedd5', text: '#c2410c' },
    pink: { bg: '#fce7f3', text: '#be185d' },
    teal: { bg: '#ccfbf1', text: '#0f766e' },
    red: { bg: '#fee2e2', text: '#b91c1c' },
    yellow: { bg: '#fef9c3', text: '#a16207' },
    indigo: { bg: '#e0e7ff', text: '#4338ca' },
    cyan: { bg: '#cffafe', text: '#0e7490' },
    gray: { bg: '#f3f4f6', text: '#374151' },
}

// Get hex colors for inline styles
const getVariableHexColors = (color?: string) => {
    return VARIABLE_HEX_COLORS[color || 'blue'] || VARIABLE_HEX_COLORS.blue
}

// Get color for variable (cycle through colors if not specified)
const getVariableColor = (variable: TemplateVariable, index: number) => {
    if (variable.color && VARIABLE_COLORS[variable.color]) {
        return VARIABLE_COLORS[variable.color]
    }
    const colorKeys = Object.keys(VARIABLE_COLORS)
    return VARIABLE_COLORS[colorKeys[index % colorKeys.length]]
}

// Default variables for email templates
const DEFAULT_TEMPLATE_VARIABLES: TemplateVariable[] = [
    {
        name: 'name',
        label: 'Name',
        description: 'Recipient name',
        color: 'blue',
    },
    {
        name: 'email',
        label: 'Email',
        description: 'Recipient email',
        color: 'green',
    },
    {
        name: 'company',
        label: 'Company',
        description: 'Company name',
        color: 'purple',
    },
    {
        name: 'phone',
        label: 'Phone',
        description: 'Phone number',
        color: 'orange',
    },
    { name: 'date', label: 'Date', description: 'Current date', color: 'pink' },
]

interface TextEditorCustomizeProps {
    value?: EditorValue
    onChange?: (value: EditorValue) => void
    disabledAttachments?: boolean
    disabled?: boolean
    readOnly?: boolean
    maxLine?: number
    className?: string
    label?: string
    placeholder?: string
    imagePrefix?: string
    attachmentPrefix?: string
    isPublic?: boolean
    /** Enable template variable insertion feature */
    enableVariables?: boolean
    /** Predefined template variables */
    templateVariables?: TemplateVariable[]
    /** Allow users to add custom variables */
    allowCustomVariables?: boolean
    /** Callback when custom variables change (includes all variables: predefined + custom) */
    onVariablesChange?: (variables: TemplateVariable[]) => void
    /** Values for template variables to show in preview */
    variableValues?: Record<string, string>
    /** Open in fullscreen mode by default */
    defaultFullscreen?: boolean
    /** Show preview panel by default (only works when defaultFullscreen is true) */
    defaultShowPreview?: boolean
}

const TextEditorCustomize: React.FC<TextEditorCustomizeProps> = ({
    value,
    onChange,
    disabledAttachments = true,
    disabled = false,
    readOnly = false,
    maxLine,
    label,
    placeholder,
    className,
    imagePrefix = 'editor-images',
    attachmentPrefix = 'editor-attachments',
    isPublic = true,
    enableVariables = false,
    templateVariables = DEFAULT_TEMPLATE_VARIABLES,
    allowCustomVariables = true,
    onVariablesChange,
    variableValues = {},
    defaultFullscreen = false,
    defaultShowPreview = false,
}) => {
    const { t } = useTranslation()
    const { preview } = useFilePreview()
    // Seed from the value prop so previously-saved attachments show on load
    // (and survive editor remounts, e.g. when template variables change).
    const [attachments, setAttachments] = useState<
        Array<{
            file: File
            url?: string
            uploading?: boolean
            progress?: number
        }>
    >(() =>
        (value?.attachments ?? []).map((a) => ({
            file: a.file,
            url: a.url,
        }))
    )
    const [isExpanded, setIsExpanded] = useState(false)
    const [isContentTruncated, setIsContentTruncated] = useState(false)
    const [isFocused, setIsFocused] = useState(false)
    const [showLinkInput, setShowLinkInput] = useState(false)
    const [linkUrl, setLinkUrl] = useState('')
    const [isFullscreen, setIsFullscreen] = useState(defaultFullscreen)
    const [showPreview, setShowPreview] = useState(defaultShowPreview)
    const [showImportHtml, setShowImportHtml] = useState(false)
    const [importHtmlValue, setImportHtmlValue] = useState('')
    const [importHtmlError, setImportHtmlError] = useState('')
    const [preserveLayout, setPreserveLayout] = useState(true)
    const [rawHtmlMode, setRawHtmlMode] = useState(false)
    const [rawHtmlContent, setRawHtmlContent] = useState('')
    const [showRawHtmlEditor, setShowRawHtmlEditor] = useState(false)
    const isEditingIframeRef = useRef(false)
    const [lineHeight, setLineHeight] = useState(1.5)
    const [showVariablePopup, setShowVariablePopup] = useState(false)
    const [customVariableName, setCustomVariableName] = useState('')
    const [customVariables, setCustomVariables] = useState<TemplateVariable[]>(
        []
    )
    const [focusCustomInput, setFocusCustomInput] = useState(false)
    const customInputRef = useRef<HTMLInputElement>(null)

    // Inline variable suggestion state
    const [showVariableSuggestion, setShowVariableSuggestion] = useState(false)
    const [suggestionFilter, setSuggestionFilter] = useState('')
    const [suggestionPosition, setSuggestionPosition] = useState({
        top: 0,
        left: 0,
    })
    const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0)
    const suggestionRef = useRef<HTMLDivElement>(null)

    // Line height constants
    const LINE_HEIGHT_MIN = 1
    const LINE_HEIGHT_MAX = 2.5
    const LINE_HEIGHT_STEP = 0.25
    const contentRef = useRef<HTMLDivElement>(null)

    // Helper function to replace variable chips with their values in preview
    const getPreviewHtml = (html: string): string => {
        if (!html || Object.keys(variableValues).length === 0) return html

        let result = html

        // Replace chip HTML format: <span data-template-variable="" data-name="varName" ...>Label</span>
        Object.entries(variableValues).forEach(([varName, value]) => {
            if (!value) return // Skip empty values

            // Match the template variable span
            const chipRegex = new RegExp(
                `<span[^>]*data-template-variable[^>]*data-name="${varName}"[^>]*>[^<]*</span>`,
                'g'
            )
            result = result.replace(
                chipRegex,
                `<span style="background-color: #dbeafe; color: #1e40af; padding: 1px 6px; border-radius: 4px; font-weight: 500;">${value}</span>`
            )

            // Also replace ${varName} format if present
            result = result.replace(
                new RegExp(`\\$\\{${varName}\\}`, 'g'),
                `<span style="background-color: #dbeafe; color: #1e40af; padding: 1px 6px; border-radius: 4px; font-weight: 500;">${value}</span>`
            )
        })

        return result
    }
    const linkInputRef = useRef<HTMLInputElement>(null)
    const linkPopupRef = useRef<HTMLDivElement>(null)
    const importHtmlRef = useRef<HTMLDivElement>(null)
    const variablePopupRef = useRef<HTMLDivElement>(null)
    const rawHtmlIframeRef = useRef<HTMLIFrameElement>(null)
    const rawHtmlContentRef = useRef<string>('')

    // Handle iframe load to enable editing
    const handleIframeLoad = (iframe: HTMLIFrameElement | null) => {
        if (!iframe?.contentDocument) return
        try {
            // Enable editing mode
            iframe.contentDocument.designMode = 'on'

            // Auto-resize iframe to fit content
            const resizeIframe = () => {
                if (iframe.contentDocument?.body) {
                    const height = iframe.contentDocument.body.scrollHeight
                    iframe.style.height = `${Math.max(height + 20, 200)}px`
                }
            }

            // Initial resize
            resizeIframe()

            // Sync content to ref only (not parent - to avoid re-render)
            const syncToRef = () => {
                if (iframe.contentDocument) {
                    const newHtml = `<!DOCTYPE html>${iframe.contentDocument.documentElement.outerHTML}`
                    rawHtmlContentRef.current = newHtml
                    resizeIframe()
                }
            }

            // Sync content to parent (only called on blur/outside click)
            const syncToParent = () => {
                if (iframe.contentDocument) {
                    const newHtml = `<!DOCTYPE html>${iframe.contentDocument.documentElement.outerHTML}`
                    rawHtmlContentRef.current = newHtml
                    onChange?.({
                        text: newHtml,
                        attachments,
                    })
                    resizeIframe()
                }
            }

            // Track when user is editing in iframe
            iframe.contentDocument.addEventListener(
                'focus',
                () => {
                    isEditingIframeRef.current = true
                },
                true
            )

            // Sync to ref on input (not to parent - to avoid re-render while typing)
            iframe.contentDocument.addEventListener('input', syncToRef, true)

            // Sync to parent on blur
            iframe.contentDocument.addEventListener(
                'blur',
                () => {
                    isEditingIframeRef.current = false
                    syncToParent()
                },
                true
            )
        } catch {
            // Cross-origin or other errors
        }
    }

    // Sync ref content to state when switching to source editor
    const handleShowSourceEditor = () => {
        if (rawHtmlContentRef.current) {
            setRawHtmlContent(rawHtmlContentRef.current)
        }
        setShowRawHtmlEditor(true)
    }

    // Insert variable into iframe at cursor position
    // In raw HTML mode, insert plain ${varName} for backend replacement (no styling)
    const insertVariableIntoIframe = (varName: string) => {
        const iframe = rawHtmlIframeRef.current
        if (!iframe?.contentDocument) return

        try {
            // Insert plain text ${varName} - backend will replace this
            iframe.contentDocument.execCommand(
                'insertText',
                false,
                `\${${varName}}`
            )

            // Sync content
            const newHtml = `<!DOCTYPE html>${iframe.contentDocument.documentElement.outerHTML}`
            rawHtmlContentRef.current = newHtml
            onChange?.({
                text: newHtml,
                attachments,
            })
        } catch {
            // Fallback: append text node
            const textNode = iframe.contentDocument.createTextNode(
                `\${${varName}}`
            )
            iframe.contentDocument.body.appendChild(textNode)
        }
    }

    // Use hooks for different upload types
    const imageUpload = useUploadFile({
        isPublic,
        prefix: imagePrefix,
        onError: (error) => {
            console.error('Image upload error:', error)
        },
    })

    const attachmentUpload = useUploadFile({
        isPublic,
        prefix: attachmentPrefix,
        onError: (error) => {
            console.error('Attachment upload error:', error)
        },
    })

    const editor = useEditor({
        extensions: [
            StarterKit,
            Bold,
            Italic,
            Underline,
            BulletList,
            OrderedList,
            Paragraph,
            CustomImage,
            Link.configure({
                openOnClick: false,
                HTMLAttributes: {
                    class: 'text-blue-600 underline hover:text-blue-800',
                },
            }),
            TextAlign.configure({ types: ['paragraph'] }),
            Dropcursor.configure({
                color: '#3b82f6',
                width: 3,
            }),
            Placeholder.configure({
                placeholder: t(
                    placeholder || 'dashboard.events.editor_placeholder'
                ),
                showOnlyCurrent: false,
                showOnlyWhenEditable: true,
            }),
            // Template variable node - only when enableVariables is true
            ...(enableVariables ? [TemplateVariableNode] : []),
        ],
        content: '',
        editable: !disabled && !readOnly,
        onUpdate: ({ editor }) => {
            let html = editor.getHTML()
            // Convert template variable nodes to ${name} format for storage
            if (enableVariables) {
                html = html.replace(
                    /<span[^>]*data-template-variable[^>]*data-name="([^"]*)"[^>]*>[^<]*<\/span>/g,
                    (_, name) => `\${${name}}`
                )
            }
            onChange?.({
                text: html,
                attachments,
            })
        },
        editorProps: {
            handleDrop: (view, event, __, moved) => {
                if (
                    !moved &&
                    event.dataTransfer &&
                    event.dataTransfer.files &&
                    event.dataTransfer.files[0]
                ) {
                    const file = event.dataTransfer.files[0]
                    if (file.type.includes('image/')) {
                        event.preventDefault()

                        const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
                        const { schema } = view.state
                        const coordinates = view.posAtCoords({
                            left: event.clientX,
                            top: event.clientY,
                        })

                        if (!coordinates) return true

                        // Get image dimensions and insert placeholder
                        const img = new Image()
                        img.onload = () => {
                            const maxWidth = 400
                            let width = img.width
                            let height = img.height
                            if (width > maxWidth) {
                                height = (maxWidth / width) * height
                                width = maxWidth
                            }
                            URL.revokeObjectURL(img.src)

                            // Insert placeholder
                            const placeholderNode =
                                schema.nodes.customImage.create({
                                    src: '',
                                    loading: true,
                                    uploadId,
                                    placeholderWidth: width,
                                    placeholderHeight: height,
                                })
                            const insertTransaction = view.state.tr.insert(
                                coordinates.pos,
                                placeholderNode
                            )
                            view.dispatch(insertTransaction)

                            // Upload the image
                            imageUpload
                                .uploadAsync([file])
                                .then((results) => {
                                    const result = results[0]
                                    if (result.status === 'fulfilled') {
                                        const imageUrl = `${import.meta.env.APP_STORAGE}/${result.value.attachment_file_url}`

                                        // Find and update placeholder
                                        view.state.doc.descendants(
                                            (node, pos) => {
                                                if (
                                                    node.type.name ===
                                                        'customImage' &&
                                                    node.attrs.uploadId ===
                                                        uploadId
                                                ) {
                                                    const updateTransaction =
                                                        view.state.tr.setNodeMarkup(
                                                            pos,
                                                            undefined,
                                                            {
                                                                ...node.attrs,
                                                                src: imageUrl,
                                                                loading: false,
                                                                uploadId: null,
                                                            }
                                                        )
                                                    view.dispatch(
                                                        updateTransaction
                                                    )
                                                    return false
                                                }
                                                return true
                                            }
                                        )
                                    } else {
                                        // Remove placeholder on failure
                                        view.state.doc.descendants(
                                            (node, pos) => {
                                                if (
                                                    node.type.name ===
                                                        'customImage' &&
                                                    node.attrs.uploadId ===
                                                        uploadId
                                                ) {
                                                    const deleteTransaction =
                                                        view.state.tr.delete(
                                                            pos,
                                                            pos + node.nodeSize
                                                        )
                                                    view.dispatch(
                                                        deleteTransaction
                                                    )
                                                    return false
                                                }
                                                return true
                                            }
                                        )
                                    }
                                })
                                .catch((error) => {
                                    console.error('Image upload error:', error)
                                    // Remove placeholder on error
                                    view.state.doc.descendants((node, pos) => {
                                        if (
                                            node.type.name === 'customImage' &&
                                            node.attrs.uploadId === uploadId
                                        ) {
                                            const deleteTransaction =
                                                view.state.tr.delete(
                                                    pos,
                                                    pos + node.nodeSize
                                                )
                                            view.dispatch(deleteTransaction)
                                            return false
                                        }
                                        return true
                                    })
                                })
                        }
                        img.onerror = () => {
                            // Fallback dimensions
                            const placeholderNode =
                                schema.nodes.customImage.create({
                                    src: '',
                                    loading: true,
                                    uploadId,
                                    placeholderWidth: 200,
                                    placeholderHeight: 150,
                                })
                            const insertTransaction = view.state.tr.insert(
                                coordinates.pos,
                                placeholderNode
                            )
                            view.dispatch(insertTransaction)

                            imageUpload.uploadAsync([file]).then((results) => {
                                const result = results[0]
                                if (result.status === 'fulfilled') {
                                    const imageUrl = `${import.meta.env.APP_STORAGE}/${result.value.attachment_file_url}`
                                    view.state.doc.descendants((node, pos) => {
                                        if (
                                            node.type.name === 'customImage' &&
                                            node.attrs.uploadId === uploadId
                                        ) {
                                            const updateTransaction =
                                                view.state.tr.setNodeMarkup(
                                                    pos,
                                                    undefined,
                                                    {
                                                        ...node.attrs,
                                                        src: imageUrl,
                                                        loading: false,
                                                        uploadId: null,
                                                    }
                                                )
                                            view.dispatch(updateTransaction)
                                            return false
                                        }
                                        return true
                                    })
                                }
                            })
                        }
                        img.src = URL.createObjectURL(file)

                        return true
                    }
                }
                return false
            },
            handleTextInput: (view, from, _to, text) => {
                // Check if variables feature is enabled and user typed '{'
                if (enableVariables && text === '{') {
                    // Check if previous character is also '{' -> open custom variable input
                    const textBefore = view.state.doc.textBetween(
                        Math.max(0, from - 1),
                        from
                    )
                    const isDoubleBrace = textBefore === '{'

                    // Get cursor position in viewport
                    const coords = view.coordsAtPos(from)
                    const editorRect = view.dom.getBoundingClientRect()

                    setSuggestionPosition({
                        top: coords.bottom - editorRect.top + 5,
                        left: coords.left - editorRect.left,
                    })
                    setShowVariableSuggestion(true)
                    setSuggestionFilter('')
                    setSelectedSuggestionIndex(0)

                    if (isDoubleBrace && allowCustomVariables) {
                        // Focus on custom input after a short delay
                        setFocusCustomInput(true)
                    }
                }
                return false // Let the default handler insert the text
            },
            handleKeyDown: (view, event) => {
                // Handle keyboard navigation in suggestion popup
                if (showVariableSuggestion && enableVariables) {
                    if (event.key === 'ArrowDown') {
                        event.preventDefault()
                        setSelectedSuggestionIndex((prev) =>
                            Math.min(
                                prev + 1,
                                filteredSuggestionVariables.length - 1
                            )
                        )
                        return true
                    }
                    if (event.key === 'ArrowUp') {
                        event.preventDefault()
                        setSelectedSuggestionIndex((prev) =>
                            Math.max(prev - 1, 0)
                        )
                        return true
                    }
                    if (event.key === 'Enter' || event.key === 'Tab') {
                        if (filteredSuggestionVariables.length > 0) {
                            event.preventDefault()
                            handleSelectSuggestion(
                                filteredSuggestionVariables[
                                    selectedSuggestionIndex
                                ].name
                            )
                            return true
                        }
                    }
                    if (event.key === 'Escape') {
                        event.preventDefault()
                        setShowVariableSuggestion(false)
                        setSuggestionFilter('')
                        return true
                    }
                    // Update filter on other key presses
                    if (event.key === 'Backspace') {
                        setSuggestionFilter((prev) => prev.slice(0, -1))
                        // Check if we should close the suggestion
                        const { from } = view.state.selection
                        const textBefore = view.state.doc.textBetween(
                            Math.max(0, from - 50),
                            from
                        )
                        if (!textBefore.includes('{')) {
                            setShowVariableSuggestion(false)
                            setSuggestionFilter('')
                        }
                    } else if (
                        event.key.length === 1 &&
                        !event.ctrlKey &&
                        !event.metaKey
                    ) {
                        setSuggestionFilter((prev) => prev + event.key)
                    }
                }
                return false
            },
            handleDOMEvents: {
                dragover: (view, event) => {
                    event.preventDefault()
                    const editorElement = view.dom
                    editorElement.classList.add('dragover')
                    return false
                },
                dragleave: (view, event) => {
                    const editorElement = view.dom
                    const rect = editorElement.getBoundingClientRect()
                    const x = event.clientX
                    const y = event.clientY

                    // Only remove dragover class if mouse is outside editor bounds
                    if (
                        x < rect.left ||
                        x > rect.right ||
                        y < rect.top ||
                        y > rect.bottom
                    ) {
                        editorElement.classList.remove('dragover')
                    }
                    return false
                },
                drop: (view) => {
                    const editorElement = view.dom
                    editorElement.classList.remove('dragover')
                    return false
                },
            },
            handlePaste: (view, event) => {
                const items = Array.from(event.clipboardData?.items || [])
                for (const item of items) {
                    if (item.type.indexOf('image') === 0) {
                        event.preventDefault()
                        const file = item.getAsFile()
                        if (file) {
                            const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
                            const { schema } = view.state

                            // Get image dimensions and insert placeholder
                            const img = new Image()
                            img.onload = () => {
                                const maxWidth = 400
                                let width = img.width
                                let height = img.height
                                if (width > maxWidth) {
                                    height = (maxWidth / width) * height
                                    width = maxWidth
                                }
                                URL.revokeObjectURL(img.src)

                                // Insert placeholder
                                const placeholderNode =
                                    schema.nodes.customImage.create({
                                        src: '',
                                        loading: true,
                                        uploadId,
                                        placeholderWidth: width,
                                        placeholderHeight: height,
                                    })
                                const insertTransaction =
                                    view.state.tr.replaceSelectionWith(
                                        placeholderNode
                                    )
                                view.dispatch(insertTransaction)

                                // Upload the image
                                imageUpload
                                    .uploadAsync([file])
                                    .then((results) => {
                                        const result = results[0]
                                        if (result.status === 'fulfilled') {
                                            const imageUrl = `${import.meta.env.APP_STORAGE}/${result.value.attachment_file_url}`

                                            // Find and update placeholder
                                            view.state.doc.descendants(
                                                (node, pos) => {
                                                    if (
                                                        node.type.name ===
                                                            'customImage' &&
                                                        node.attrs.uploadId ===
                                                            uploadId
                                                    ) {
                                                        const updateTransaction =
                                                            view.state.tr.setNodeMarkup(
                                                                pos,
                                                                undefined,
                                                                {
                                                                    ...node.attrs,
                                                                    src: imageUrl,
                                                                    loading: false,
                                                                    uploadId:
                                                                        null,
                                                                }
                                                            )
                                                        view.dispatch(
                                                            updateTransaction
                                                        )
                                                        return false
                                                    }
                                                    return true
                                                }
                                            )
                                        } else {
                                            // Remove placeholder on failure
                                            view.state.doc.descendants(
                                                (node, pos) => {
                                                    if (
                                                        node.type.name ===
                                                            'customImage' &&
                                                        node.attrs.uploadId ===
                                                            uploadId
                                                    ) {
                                                        const deleteTransaction =
                                                            view.state.tr.delete(
                                                                pos,
                                                                pos +
                                                                    node.nodeSize
                                                            )
                                                        view.dispatch(
                                                            deleteTransaction
                                                        )
                                                        return false
                                                    }
                                                    return true
                                                }
                                            )
                                        }
                                    })
                                    .catch((error) => {
                                        console.error(
                                            'Image upload error:',
                                            error
                                        )
                                        // Remove placeholder on error
                                        view.state.doc.descendants(
                                            (node, pos) => {
                                                if (
                                                    node.type.name ===
                                                        'customImage' &&
                                                    node.attrs.uploadId ===
                                                        uploadId
                                                ) {
                                                    const deleteTransaction =
                                                        view.state.tr.delete(
                                                            pos,
                                                            pos + node.nodeSize
                                                        )
                                                    view.dispatch(
                                                        deleteTransaction
                                                    )
                                                    return false
                                                }
                                                return true
                                            }
                                        )
                                    })
                            }
                            img.onerror = () => {
                                // Fallback dimensions
                                const placeholderNode =
                                    schema.nodes.customImage.create({
                                        src: '',
                                        loading: true,
                                        uploadId,
                                        placeholderWidth: 200,
                                        placeholderHeight: 150,
                                    })
                                const insertTransaction =
                                    view.state.tr.replaceSelectionWith(
                                        placeholderNode
                                    )
                                view.dispatch(insertTransaction)

                                imageUpload
                                    .uploadAsync([file])
                                    .then((results) => {
                                        const result = results[0]
                                        if (result.status === 'fulfilled') {
                                            const imageUrl = `${import.meta.env.APP_STORAGE}/${result.value.attachment_file_url}`
                                            view.state.doc.descendants(
                                                (node, pos) => {
                                                    if (
                                                        node.type.name ===
                                                            'customImage' &&
                                                        node.attrs.uploadId ===
                                                            uploadId
                                                    ) {
                                                        const updateTransaction =
                                                            view.state.tr.setNodeMarkup(
                                                                pos,
                                                                undefined,
                                                                {
                                                                    ...node.attrs,
                                                                    src: imageUrl,
                                                                    loading: false,
                                                                    uploadId:
                                                                        null,
                                                                }
                                                            )
                                                        view.dispatch(
                                                            updateTransaction
                                                        )
                                                        return false
                                                    }
                                                    return true
                                                }
                                            )
                                        }
                                    })
                            }
                            img.src = URL.createObjectURL(file)
                        }
                        return true
                    }
                }
                return false
            },
            attributes: {
                class: 'focus:outline-none',
            },
        },
    })

    const handleUploadImage = async () => {
        if (disabled || readOnly) return
        const input = document.createElement('input')
        input.type = 'file'
        input.accept = 'image/*'
        input.onchange = async (event) => {
            const file = (event.target as HTMLInputElement).files?.[0]
            if (!file) return

            // Generate unique upload ID
            const uploadId = `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

            // Get image dimensions for placeholder
            const getImageDimensions = (
                file: File
            ): Promise<{ width: number; height: number }> => {
                return new Promise((resolve) => {
                    const img = new Image()
                    img.onload = () => {
                        // Limit max width to 400px while maintaining aspect ratio
                        const maxWidth = 400
                        let width = img.width
                        let height = img.height
                        if (width > maxWidth) {
                            height = (maxWidth / width) * height
                            width = maxWidth
                        }
                        resolve({ width, height })
                        URL.revokeObjectURL(img.src)
                    }
                    img.onerror = () => resolve({ width: 200, height: 150 })
                    img.src = URL.createObjectURL(file)
                })
            }

            const dimensions = await getImageDimensions(file)

            // Insert placeholder image with loading state
            editor
                ?.chain()
                .focus()
                .insertContent({
                    type: 'customImage',
                    attrs: {
                        src: '',
                        loading: true,
                        uploadId,
                        placeholderWidth: dimensions.width,
                        placeholderHeight: dimensions.height,
                    },
                })
                .run()

            try {
                // Upload file using hook
                const results = await imageUpload.uploadAsync([file])
                const result = results[0]

                if (result.status === 'fulfilled') {
                    const imageUrl = `${import.meta.env.APP_STORAGE}/${result.value.attachment_file_url}`

                    // Find and update the placeholder node
                    editor?.state.doc.descendants((node, pos) => {
                        if (
                            node.type.name === 'customImage' &&
                            node.attrs.uploadId === uploadId
                        ) {
                            editor
                                .chain()
                                .focus()
                                .setNodeSelection(pos)
                                .updateAttributes('customImage', {
                                    src: imageUrl,
                                    loading: false,
                                    uploadId: null,
                                })
                                .run()
                            return false // Stop iteration
                        }
                        return true
                    })
                } else {
                    // Remove placeholder on failure
                    editor?.state.doc.descendants((node, pos) => {
                        if (
                            node.type.name === 'customImage' &&
                            node.attrs.uploadId === uploadId
                        ) {
                            editor
                                .chain()
                                .focus()
                                .setNodeSelection(pos)
                                .deleteSelection()
                                .run()
                            return false
                        }
                        return true
                    })
                }
            } catch (error) {
                console.error('Image upload error:', error)
                // Remove placeholder on error
                editor?.state.doc.descendants((node, pos) => {
                    if (
                        node.type.name === 'customImage' &&
                        node.attrs.uploadId === uploadId
                    ) {
                        editor
                            .chain()
                            .focus()
                            .setNodeSelection(pos)
                            .deleteSelection()
                            .run()
                        return false
                    }
                    return true
                })
            }

            input.remove()
        }
        input.click()
    }

    const handleSelectAttachments = async () => {
        if (disabled || disabledAttachments || readOnly) return
        const input = document.createElement('input')
        input.type = 'file'
        input.multiple = true
        input.onchange = async (event) => {
            const files = (event.target as HTMLInputElement).files
            if (!files) return

            const filesArray = Array.from(files)

            // Add files to attachments with uploading state
            const newFiles = filesArray.map((file: File) => ({
                file,
                uploading: true,
                progress: 0,
            }))

            const updatedAttachments = [...attachments, ...newFiles]
            setAttachments(updatedAttachments)

            try {
                // Upload files using hook
                const results = await attachmentUpload.uploadAsync(filesArray)

                // Update attachments based on results
                setAttachments((prev) => {
                    const updated = [...prev]
                    results.forEach((result, index) => {
                        const attachmentIndex =
                            prev.length - filesArray.length + index
                        if (result.status === 'fulfilled') {
                            updated[attachmentIndex] = {
                                ...updated[attachmentIndex],
                                uploading: false,
                                url: `${import.meta.env.APP_STORAGE}/${result.value.attachment_file_url}`,
                                progress: 100,
                            }
                        } else {
                            // Remove failed uploads
                            updated.splice(attachmentIndex, 1)
                        }
                    })
                    return updated
                })
            } catch (error) {
                // Remove all failed uploads
                setAttachments((prev) =>
                    prev.slice(0, prev.length - filesArray.length)
                )
                console.error('Attachment upload error:', error)
            }

            input.remove()
        }
        input.click()
    }

    const handleSetLink = () => {
        if (disabled || readOnly) return

        // Check if text is selected
        const { from, to } = editor?.state.selection || { from: 0, to: 0 }
        const hasSelection = from !== to

        if (!hasSelection && !editor?.isActive('link')) {
            return // Don't show link input if no text is selected
        }

        const previousUrl = editor?.getAttributes('link').href || ''
        setLinkUrl(previousUrl)
        setShowLinkInput(true)

        // Focus input after render
        setTimeout(() => {
            linkInputRef.current?.focus()
        }, 100)
    }

    const handleApplyLink = () => {
        if (!linkUrl.trim()) {
            // Empty - remove link
            editor?.chain().focus().extendMarkRange('link').unsetLink().run()
        } else {
            // Add https:// if no protocol
            let url = linkUrl.trim()
            if (!/^https?:\/\//i.test(url)) {
                url = 'https://' + url
            }
            editor
                ?.chain()
                .focus()
                .extendMarkRange('link')
                .setLink({ href: url })
                .run()
        }
        setShowLinkInput(false)
        setLinkUrl('')
    }

    const handleRemoveLink = () => {
        editor?.chain().focus().extendMarkRange('link').unsetLink().run()
        setShowLinkInput(false)
        setLinkUrl('')
    }

    const handleOpenLink = () => {
        if (linkUrl) {
            let url = linkUrl.trim()
            if (!/^https?:\/\//i.test(url)) {
                url = 'https://' + url
            }
            window.open(url, '_blank')
        }
    }

    // Validate HTML format
    const validateHtml = (html: string): { valid: boolean; error?: string } => {
        if (!html.trim()) {
            return {
                valid: false,
                error: t('textEditor.htmlEmpty') || 'HTML content is empty',
            }
        }

        try {
            const parser = new DOMParser()
            const doc = parser.parseFromString(html, 'text/html')

            // Check for parsing errors
            const parserError = doc.querySelector('parsererror')
            if (parserError) {
                return {
                    valid: false,
                    error:
                        t('textEditor.htmlParseError') || 'Invalid HTML format',
                }
            }

            // Check if it has any meaningful content
            const body = doc.body
            if (
                !body ||
                (!body.textContent?.trim() &&
                    !body.querySelector('img, video, iframe'))
            ) {
                return {
                    valid: false,
                    error:
                        t('textEditor.htmlNoContent') || 'HTML has no content',
                }
            }

            // Check for dangerous tags
            const dangerousTags = [
                'script',
                'iframe',
                'object',
                'embed',
                'form',
            ]
            for (const tag of dangerousTags) {
                if (doc.querySelector(tag)) {
                    return {
                        valid: false,
                        error: `${t('textEditor.htmlDangerousTag') || 'Dangerous tag found'}: <${tag}>`,
                    }
                }
            }

            return { valid: true }
        } catch {
            return {
                valid: false,
                error: t('textEditor.htmlParseError') || 'Failed to parse HTML',
            }
        }
    }

    // Sanitize HTML - remove dangerous attributes and tags, inline styles from <style> tags
    const sanitizeHtml = (html: string): string => {
        const parser = new DOMParser()
        const doc = parser.parseFromString(html, 'text/html')

        // Extract and parse styles from <style> tags
        const styleTags = doc.querySelectorAll('style')
        const styleRules: { selector: string; styles: string }[] = []

        styleTags.forEach((styleTag) => {
            const cssText = styleTag.textContent || ''
            // Simple CSS parser - extract selector and styles
            const ruleRegex = /([^{]+)\s*\{\s*([^}]*)\s*\}/g
            let match
            while ((match = ruleRegex.exec(cssText)) !== null) {
                const selector = match[1].trim()
                const styles = match[2].trim()
                // Skip @media queries and other at-rules for now
                if (!selector.startsWith('@') && selector && styles) {
                    // Handle multiple selectors separated by comma
                    selector.split(',').forEach((sel) => {
                        styleRules.push({
                            selector: sel.trim(),
                            styles: styles,
                        })
                    })
                }
            }
        })

        // Apply styles inline to matching elements
        styleRules.forEach(({ selector, styles }) => {
            try {
                // Skip pseudo-selectors and complex selectors that querySelector can't handle
                if (
                    selector.includes(':') ||
                    selector.includes('[x-apple') ||
                    selector.includes('#MessageViewBody')
                ) {
                    return
                }
                const elements = doc.body.querySelectorAll(selector)
                elements.forEach((el) => {
                    const existingStyle = el.getAttribute('style') || ''
                    // Merge styles, existing inline styles take precedence
                    const mergedStyle = existingStyle
                        ? `${styles}; ${existingStyle}`
                        : styles
                    el.setAttribute('style', mergedStyle)
                })
            } catch {
                // Invalid selector, skip
            }
        })

        // Remove dangerous attributes
        const dangerousAttrs = [
            'onclick',
            'onerror',
            'onload',
            'onmouseover',
            'onfocus',
            'onblur',
        ]
        const allElements = doc.body.querySelectorAll('*')
        allElements.forEach((el) => {
            dangerousAttrs.forEach((attr) => el.removeAttribute(attr))
            // Remove javascript: URLs
            if (el.getAttribute('href')?.startsWith('javascript:')) {
                el.removeAttribute('href')
            }
            if (el.getAttribute('src')?.startsWith('javascript:')) {
                el.removeAttribute('src')
            }
        })

        return doc.body.innerHTML
    }

    // Sanitize full HTML document for iframe rendering (preserves <html>, <head>, <body> structure)
    const sanitizeFullHtml = (html: string): string => {
        const parser = new DOMParser()
        const doc = parser.parseFromString(html, 'text/html')

        // Remove dangerous elements
        const dangerousTags = ['script', 'iframe', 'object', 'embed']
        dangerousTags.forEach((tag) => {
            const elements = doc.querySelectorAll(tag)
            elements.forEach((el) => el.remove())
        })

        // Remove dangerous attributes
        const dangerousAttrs = [
            'onclick',
            'onerror',
            'onload',
            'onmouseover',
            'onfocus',
            'onblur',
        ]
        const allElements = doc.querySelectorAll('*')
        allElements.forEach((el) => {
            dangerousAttrs.forEach((attr) => el.removeAttribute(attr))
            // Remove javascript: URLs
            if (el.getAttribute('href')?.startsWith('javascript:')) {
                el.removeAttribute('href')
            }
            if (el.getAttribute('src')?.startsWith('javascript:')) {
                el.removeAttribute('src')
            }
        })

        // Return full HTML document
        return `<!DOCTYPE html>${doc.documentElement.outerHTML}`
    }

    const handleImportHtml = () => {
        setImportHtmlError('')
        const validation = validateHtml(importHtmlValue)

        if (!validation.valid) {
            setImportHtmlError(validation.error || 'Invalid HTML')
            return
        }

        if (preserveLayout) {
            // Preserve layout mode: store full HTML and switch to raw HTML view
            const sanitized = sanitizeFullHtml(importHtmlValue)
            setRawHtmlContent(sanitized)
            setRawHtmlMode(true)
            // Notify parent of change
            onChange?.({
                text: sanitized,
                attachments,
            })
        } else {
            // Normal mode: process through TipTap
            const sanitized = sanitizeHtml(importHtmlValue)
            editor?.chain().focus().setContent(sanitized).run()
        }

        setShowImportHtml(false)
        setImportHtmlValue('')
        setImportHtmlError('')
    }

    const handleImportHtmlFile = () => {
        const input = document.createElement('input')
        input.type = 'file'
        input.accept = '.html,.htm,text/html'
        input.onchange = async (event) => {
            const file = (event.target as HTMLInputElement).files?.[0]
            if (!file) return

            try {
                const text = await file.text()
                setImportHtmlValue(text)
                setImportHtmlError('')

                // Auto validate on file import
                const validation = validateHtml(text)
                if (!validation.valid) {
                    setImportHtmlError(validation.error || 'Invalid HTML')
                }
            } catch {
                setImportHtmlError(
                    t('textEditor.htmlFileReadError') || 'Failed to read file'
                )
            }

            input.remove()
        }
        input.click()
    }

    const handleIncreaseLineHeight = () => {
        setLineHeight((prev) =>
            Math.min(prev + LINE_HEIGHT_STEP, LINE_HEIGHT_MAX)
        )
    }

    const handleDecreaseLineHeight = () => {
        setLineHeight((prev) =>
            Math.max(prev - LINE_HEIGHT_STEP, LINE_HEIGHT_MIN)
        )
    }

    // Insert variable at cursor position
    const handleInsertVariable = (variableName: string) => {
        if (disabled || readOnly) return

        // Find the variable to get its label and color
        const allVars = [...templateVariables, ...customVariables]
        const variable = allVars.find((v) => v.name === variableName)
        const label = variable?.label || variableName
        const color = variable?.color || 'blue'

        // If in raw HTML mode, insert plain ${varName} into iframe
        if (rawHtmlMode && !showRawHtmlEditor) {
            insertVariableIntoIframe(variableName)
            setShowVariablePopup(false)
            return
        }

        // Normal mode: Insert as a custom node that displays label but saves as ${name}
        if (!editor) return
        editor
            .chain()
            .focus()
            .insertContent({
                type: 'templateVariable',
                attrs: {
                    name: variableName,
                    label: label,
                    color: color,
                },
            })
            .run()
        setShowVariablePopup(false)
    }

    // Add custom variable (from toolbar popup)
    const handleAddCustomVariable = () => {
        if (!customVariableName.trim()) return

        const varName = generateVariableName(customVariableName)

        // Check if variable already exists
        const allVariables = [...templateVariables, ...customVariables]
        if (allVariables.some((v) => v.name === varName)) {
            // Variable already exists, just insert it
            handleInsertVariable(varName)
            setCustomVariableName('')
            return
        }

        // Add new custom variable
        const newVariable: TemplateVariable = {
            name: varName,
            label: customVariableName.trim(),
            description: 'Custom variable',
        }
        setCustomVariables((prev) => [...prev, newVariable])
        handleInsertVariable(varName)
        setCustomVariableName('')
    }

    // Add custom variable from inline popup (deletes { or {{ before inserting)
    const handleAddCustomVariableInline = () => {
        if (!customVariableName.trim() || !editor) return

        const varName = generateVariableName(customVariableName)

        // Add to custom variables if not exists
        const allVars = [...templateVariables, ...customVariables]
        if (!allVars.some((v) => v.name === varName)) {
            const newVariable: TemplateVariable = {
                name: varName,
                label: customVariableName.trim(),
                description: 'Custom variable',
            }
            setCustomVariables((prev) => [...prev, newVariable])
        }

        // Delete the { or {{ and insert the variable
        const { from } = editor.state.selection
        const textBefore = editor.state.doc.textBetween(
            Math.max(0, from - 50),
            from
        )
        const lastBraceIndex = textBefore.lastIndexOf('{')

        if (lastBraceIndex !== -1) {
            // Check if there's a {{ (double brace) - need to delete both
            const hasDoubleBrace =
                lastBraceIndex > 0 && textBefore[lastBraceIndex - 1] === '{'
            const deleteFrom =
                from -
                (textBefore.length - lastBraceIndex) -
                (hasDoubleBrace ? 1 : 0)

            // Find variable to get label and color
            const variable = allVars.find((v) => v.name === varName) || {
                name: varName,
                label: customVariableName.trim(),
                color: 'blue' as const,
            }

            editor
                .chain()
                .focus()
                .deleteRange({ from: deleteFrom, to: from })
                .insertContent({
                    type: 'templateVariable',
                    attrs: {
                        name: varName,
                        label: variable.label,
                        color: variable.color || 'blue',
                    },
                })
                .run()
        }

        setCustomVariableName('')
        setShowVariableSuggestion(false)
    }

    // All available variables (predefined + custom)
    const allVariables = [...templateVariables, ...customVariables]

    // Notify parent when variables change (predefined + custom)
    useEffect(() => {
        if (onVariablesChange && enableVariables) {
            onVariablesChange(allVariables)
        }
    }, [customVariables, templateVariables, enableVariables])

    // Filtered variables for inline suggestion (normalize Vietnamese for search)
    const filteredSuggestionVariables = allVariables.filter((v) => {
        const normalizedFilter = removeVietnameseDiacritics(
            suggestionFilter?.toLowerCase() || ''
        )
        const normalizedName = removeVietnameseDiacritics(
            v.name?.toLowerCase() || ''
        )
        const normalizedLabel = removeVietnameseDiacritics(
            v.label?.toLowerCase() || ''
        )
        return (
            normalizedName.includes(normalizedFilter) ||
            normalizedLabel.includes(normalizedFilter)
        )
    })

    // Handle inline variable selection
    const handleSelectSuggestion = (variableName: string) => {
        if (!editor) return

        // Get current cursor position and find the { character
        const { from } = editor.state.selection
        const textBefore = editor.state.doc.textBetween(
            Math.max(0, from - 50),
            from
        )
        const lastBraceIndex = textBefore.lastIndexOf('{')

        if (lastBraceIndex !== -1) {
            const deleteFrom = from - (textBefore.length - lastBraceIndex)
            editor
                .chain()
                .focus()
                .deleteRange({ from: deleteFrom, to: from })
                .insertContent(`\${${variableName}}`)
                .run()
        }

        setShowVariableSuggestion(false)
        setSuggestionFilter('')
        setSelectedSuggestionIndex(0)
    }

    // Close suggestion on click outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                showVariableSuggestion &&
                suggestionRef.current &&
                !suggestionRef.current.contains(e.target as globalThis.Node)
            ) {
                setShowVariableSuggestion(false)
                setSuggestionFilter('')
                setSelectedSuggestionIndex(0)
                setFocusCustomInput(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () =>
            document.removeEventListener('mousedown', handleClickOutside)
    }, [showVariableSuggestion])

    // Focus custom input when {{ is typed
    useEffect(() => {
        if (focusCustomInput && customInputRef.current) {
            customInputRef.current.focus()
            setFocusCustomInput(false)
        }
    }, [focusCustomInput])

    // Update progress for attachments
    useEffect(() => {
        if (attachmentUpload.percent) {
            setAttachments((prev) =>
                prev.map((item) => {
                    if (
                        item.uploading &&
                        attachmentUpload.percent[item.file.name]
                    ) {
                        return {
                            ...item,
                            progress: attachmentUpload.percent[item.file.name],
                        }
                    }
                    return item
                })
            )
        }
    }, [attachmentUpload.percent])

    // Extract images from HTML content
    const extractImagesFromHTML = (htmlContent: string) => {
        const parser = new DOMParser()
        const doc = parser.parseFromString(htmlContent, 'text/html')
        const images = doc.querySelectorAll('img')
        return Array.from(images).map((img) => ({
            src: img.src,
            alt: img.alt || 'Image',
            title: img.title || '',
        }))
    }

    // Get file extension and appropriate icon
    const getFileExtension = (fileName: string) => {
        return fileName?.split('.').pop()?.toLowerCase() || ''
    }

    const getFileIcon = (fileName: string) => {
        const ext = getFileExtension(fileName)
        const iconMap: Record<string, React.FC> = {
            pdf: PdfIcons,
            jpg: ImageIcons,
            jpeg: ImageIcons,
            png: ImageIcons,
            gif: ImageIcons,
            webp: ImageIcons,
            txt: TextIcons,
            csv: CsvIcons,
        }
        return iconMap[ext] || TextIcons
    }

    const handlePreviewFile = (url: string, fileName: string) => {
        preview(url, undefined, {
            attachment_file_name: fileName,
            attachment_file_url: url.replace(
                `${import.meta.env.APP_STORAGE}/`,
                ''
            ),
            attachment_file_type: `file/${getFileExtension(fileName)}`,
        })
    }
    interface ToolItem {
        icon: React.ReactElement
        action: () => void
        active: boolean | null
        title?: string
        type?: 'button' | 'select'
        options?: { label: string; value: string }[]
        currentValue?: string
    }

    const tools: ToolItem[] = [
        {
            icon: <UndoIcons className="w-4 h-4" />,
            action: () => editor?.chain().focus().undo().run(),
            active: null,
            title: t('textEditor.undo') || 'Undo',
        },
        {
            icon: <RedoIcons className="w-4 h-4" />,
            action: () => editor?.chain().focus().redo().run(),
            active: null,
            title: t('textEditor.redo') || 'Redo',
        },
        {
            icon: <BoldIcon className="w-4 h-4" />,
            action: () => editor?.chain().focus().toggleBold().run(),
            active: editor?.isActive('bold') || false,
            title: t('textEditor.bold') || 'Bold',
        },
        {
            icon: <ItalicIcon className="w-4 h-4" />,
            action: () => editor?.chain().focus().toggleItalic().run(),
            active: editor?.isActive('italic') || false,
            title: t('textEditor.italic') || 'Italic',
        },
        {
            icon: <UnderlineIcon className="w-4 h-4" />,
            action: () => editor?.chain().focus().toggleUnderline().run(),
            active: editor?.isActive('underline') || false,
            title: t('textEditor.underline') || 'Underline',
        },
        {
            icon: <ListNumberIcon className="w-4 h-4" />,
            action: () => editor?.chain().focus().toggleOrderedList().run(),
            active: editor?.isActive('orderedList') || false,
            title: t('textEditor.orderedList') || 'Numbered List',
        },
        {
            icon: <ListBulletIcon className="w-4 h-4" />,
            action: () => editor?.chain().focus().toggleBulletList().run(),
            active: editor?.isActive('bulletList') || false,
            title: t('textEditor.bulletList') || 'Bullet List',
        },
        {
            icon: <AlignLeftIcon className="w-4 h-4" />,
            action: () => editor?.chain().focus().setTextAlign('left').run(),
            active: editor?.isActive({ textAlign: 'left' }) || false,
            title: t('textEditor.alignLeft') || 'Align Left',
        },
        {
            icon: <AlignCenterIcon className="w-4 h-4" />,
            action: () => editor?.chain().focus().setTextAlign('center').run(),
            active: editor?.isActive({ textAlign: 'center' }) || false,
            title: t('textEditor.alignCenter') || 'Align Center',
        },
        {
            icon: <AlignRightIcon className="w-4 h-4" />,
            action: () => editor?.chain().focus().setTextAlign('right').run(),
            active: editor?.isActive({ textAlign: 'right' }) || false,
            title: t('textEditor.alignRight') || 'Align Right',
        },
        {
            icon: <AlignJustifyIcon className="w-4 h-4" />,
            action: () => editor?.chain().focus().setTextAlign('justify').run(),
            active: editor?.isActive({ textAlign: 'justify' }) || false,
            title: t('textEditor.alignJustify') || 'Justify',
        },
        {
            icon: <LinkIcon className="w-4 h-4" />,
            action: handleSetLink,
            active: editor?.isActive('link') || false,
            title: t('textEditor.link') || 'Insert Link',
        },
        {
            icon: <CodeBlockIcon className="w-4 h-4" />,
            action: () => editor?.chain().focus().toggleCodeBlock().run(),
            active: editor?.isActive('codeBlock') || false,
            title: t('textEditor.codeBlock') || 'Code Block',
        },
        {
            icon: <LineHeightDecreaseIcon className="w-4 h-4" />,
            action: handleDecreaseLineHeight,
            active: lineHeight <= LINE_HEIGHT_MIN,
            title: `${t('textEditor.decreaseLineHeight') || 'Decrease line height'} (${lineHeight.toFixed(2)})`,
        },
        {
            icon: <LineHeightIncreaseIcon className="w-4 h-4" />,
            action: handleIncreaseLineHeight,
            active: lineHeight >= LINE_HEIGHT_MAX,
            title: `${t('textEditor.increaseLineHeight') || 'Increase line height'} (${lineHeight.toFixed(2)})`,
        },
        ...(enableVariables
            ? [
                  {
                      icon: <VariableIcon className="w-4 h-4" />,
                      action: () => setShowVariablePopup(!showVariablePopup),
                      active: showVariablePopup,
                      title:
                          t('textEditor.insertVariable') || 'Insert Variable',
                  },
              ]
            : []),
        ...(disabledAttachments
            ? []
            : [
                  {
                      icon: <AttachIcon className="w-4 h-4" />,
                      action: handleSelectAttachments,
                      active: null,
                      title: t('textEditor.attachment') || 'Attach File',
                  },
              ]),
        {
            icon: <ImageIcon className="w-4 h-4" />,
            action: handleUploadImage,
            active: null,
            title: t('textEditor.image') || 'Insert Image',
        },
        {
            icon: <ImportHtmlIcon className="w-4 h-4" />,
            action: () => setShowImportHtml(!showImportHtml),
            active: showImportHtml,
            title: t('textEditor.importHtml') || 'Import HTML',
        },
        {
            icon: <PreviewIcon className="w-4 h-4" />,
            action: () => {
                if (!isFullscreen) {
                    setIsFullscreen(true)
                }
                setShowPreview(!showPreview)
            },
            active: showPreview,
            title: t('textEditor.preview') || 'Preview',
        },
        {
            icon: isFullscreen ? (
                <MinimizeIcons className="w-3 h-3" />
            ) : (
                <MaximizeIcons className="w-3 h-3" />
            ),
            action: () => {
                if (isFullscreen) {
                    setShowPreview(false)
                }
                setIsFullscreen(!isFullscreen)
            },
            active: isFullscreen,
            title: isFullscreen
                ? t('textEditor.exitFullscreen') || 'Exit Fullscreen'
                : t('textEditor.fullscreen') || 'Fullscreen',
        },
    ]

    useEffect(() => {
        if (editor) {
            editor.storage.customImage.readOnly = readOnly
        }
    }, [editor, readOnly])

    // Keep the latest onChange without making it an effect dependency, so a new
    // inline handler from the parent on every render can't retrigger the effect
    // below (which would cause a re-render loop once attachments are seeded).
    const onChangeRef = useRef(onChange)
    useEffect(() => {
        onChangeRef.current = onChange
    })

    // Notify the parent only when the attachment list actually changes (user
    // adds/removes a file) — not on the initial seed or when the editor first
    // becomes ready, both of which would emit stale/empty editor content.
    const prevAttachmentsRef = useRef(attachments)
    useEffect(() => {
        if (prevAttachmentsRef.current === attachments) return
        prevAttachmentsRef.current = attachments
        if (attachments?.length > 0 && editor) {
            onChangeRef.current?.({
                text: editor.getHTML() || '',
                attachments,
            })
        }
    }, [attachments, editor])

    // Helper to detect if content is full HTML document
    const isFullHtmlDocument = (html: string): boolean => {
        const trimmed = html.trim().toLowerCase()
        return (
            trimmed.startsWith('<!doctype') ||
            trimmed.startsWith('<html') ||
            (trimmed.includes('<head') && trimmed.includes('<body'))
        )
    }

    useEffect(() => {
        if (!editor || !value?.text) return

        // Detect if content is full HTML document (from preserveLayout import)
        if (isFullHtmlDocument(value.text)) {
            // Switch to raw HTML mode
            setRawHtmlMode(true)
            // Don't update state if user is currently editing in iframe (to avoid re-render)
            // Also check if content is actually different from ref
            if (
                !isEditingIframeRef.current &&
                rawHtmlContentRef.current !== value.text
            ) {
                setRawHtmlContent(value.text)
                rawHtmlContentRef.current = value.text
            }
            return
        }

        // Normal TipTap mode
        if (rawHtmlMode) {
            // Reset raw HTML mode if content is not full HTML
            setRawHtmlMode(false)
            setRawHtmlContent('')
            rawHtmlContentRef.current = ''
        }

        const { from, to } = editor.state.selection

        let content = value.text
        // Convert ${name} patterns to template variable nodes when loading
        if (enableVariables) {
            const allVars = [...templateVariables, ...customVariables]
            content = content.replace(/\$\{([^}]+)\}/g, (_, varName) => {
                const variable = allVars.find((v) => v.name === varName)
                const label = variable?.label || varName
                const color = variable?.color || 'blue'
                const hexColors = getVariableHexColors(color)
                return `<span data-template-variable="" data-name="${varName}" data-label="${label}" data-color="${color}" class="template-variable-chip" style="display: inline; background-color: ${hexColors.bg}; color: ${hexColors.text}; padding: 1px 5px; border-radius: 3px; font-size: 0.85em; cursor: default; user-select: none;">${label}</span>`
            })
        }

        editor.commands.setContent(content, false, {
            preserveWhitespace: 'full',
        })
        editor.commands.setTextSelection({ from, to })
    }, [
        editor,
        value?.text,
        enableVariables,
        templateVariables,
        customVariables,
    ])

    // Check if content is actually truncated
    useEffect(() => {
        if (readOnly && maxLine && contentRef.current && !isExpanded) {
            // Get the text content to check if it's long enough
            const textContent = editor?.getText() || ''
            const lines = textContent.split('\n').length
            const isLongContent =
                lines > maxLine || textContent.length > maxLine * 50 // rough estimate
            setIsContentTruncated(isLongContent)
        }
    }, [readOnly, maxLine, value?.text, isExpanded, editor])

    // Handle ESC key to close fullscreen
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Don't handle ESC if user is typing in an input
            const target = e.target as HTMLElement
            if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
                return
            }
            if (e.key === 'Escape' && isFullscreen) {
                setIsFullscreen(false)
                setShowPreview(false)
            }
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [isFullscreen])

    // Handle click outside to close link popup
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                showLinkInput &&
                linkPopupRef.current &&
                !linkPopupRef.current.contains(e.target as globalThis.Node)
            ) {
                setShowLinkInput(false)
                setLinkUrl('')
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () =>
            document.removeEventListener('mousedown', handleClickOutside)
    }, [showLinkInput])

    // Handle click outside to close import HTML popup
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                showImportHtml &&
                importHtmlRef.current &&
                !importHtmlRef.current.contains(e.target as globalThis.Node)
            ) {
                setShowImportHtml(false)
                setImportHtmlValue('')
                setImportHtmlError('')
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () =>
            document.removeEventListener('mousedown', handleClickOutside)
    }, [showImportHtml])

    // Handle click outside to close variable popup
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                showVariablePopup &&
                variablePopupRef.current &&
                !variablePopupRef.current.contains(e.target as globalThis.Node)
            ) {
                setShowVariablePopup(false)
                setCustomVariableName('')
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () =>
            document.removeEventListener('mousedown', handleClickOutside)
    }, [showVariablePopup])

    if (!editor) return null

    // Calculate if content should be truncated in readOnly mode
    const shouldTruncate =
        readOnly && maxLine && !isExpanded && isContentTruncated

    const hasContent = value?.text && editor?.getText().trim().length > 0
    const shouldShrinkLabel = isFocused || hasContent

    // Fullscreen modal render
    if (isFullscreen && !readOnly) {
        return (
            <div className="fixed inset-0 z-[9999] bg-white flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-0.5 border-b">
                    {label && (
                        <span className="text-lg font-medium text-gray-800">
                            {label}
                        </span>
                    )}
                    <button
                        onClick={() => {
                            setIsFullscreen(false)
                            setShowPreview(false)
                        }}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        title={t('close')}
                    >
                        <CloseIcons className="w-4 h-4" />
                    </button>
                </div>

                {/* Editor Content - Split view on md/lg when preview is enabled */}
                <div
                    className={`flex-1 overflow-hidden flex ${showPreview ? 'flex-col md:flex-row' : ''}`}
                >
                    {/* Editor */}
                    <div
                        className={`overflow-auto p-6 relative ${showPreview ? 'w-full md:w-1/2 md:border-r border-gray-200' : 'w-full'}`}
                        style={{
                            ['--editor-line-height' as string]: lineHeight,
                        }}
                    >
                        {rawHtmlMode ? (
                            /* Raw HTML Mode - editable iframe */
                            <div
                                className="w-full h-full flex flex-col"
                                data-editor-action="raw-html-container"
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-gray-700">
                                        {t('textEditor.htmlPreview') ||
                                            'HTML Preview (Preserved Layout)'}
                                    </span>
                                    <div className="flex gap-2">
                                        <button
                                            data-editor-action="toggle-source"
                                            onClick={() =>
                                                showRawHtmlEditor
                                                    ? setShowRawHtmlEditor(
                                                          false
                                                      )
                                                    : handleShowSourceEditor()
                                            }
                                            className="text-xs text-blue-600 hover:text-blue-800"
                                        >
                                            {showRawHtmlEditor
                                                ? t('textEditor.showPreview') ||
                                                  'Show Preview'
                                                : t('textEditor.editSource') ||
                                                  'Edit Source'}
                                        </button>
                                        <button
                                            onClick={() => {
                                                setRawHtmlMode(false)
                                                setRawHtmlContent('')
                                                rawHtmlContentRef.current = ''
                                                setShowRawHtmlEditor(false)
                                                editor
                                                    ?.chain()
                                                    .focus()
                                                    .setContent('')
                                                    .run()
                                            }}
                                            className="text-xs text-red-600 hover:text-red-800"
                                        >
                                            {t('textEditor.exitRawMode') ||
                                                'Exit Raw Mode'}
                                        </button>
                                    </div>
                                </div>
                                {showRawHtmlEditor ? (
                                    <textarea
                                        value={rawHtmlContent}
                                        onChange={(e) => {
                                            setRawHtmlContent(e.target.value)
                                            rawHtmlContentRef.current =
                                                e.target.value
                                            onChange?.({
                                                text: e.target.value,
                                                attachments,
                                            })
                                        }}
                                        className="flex-1 w-full p-4 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono resize-none"
                                    />
                                ) : (
                                    <iframe
                                        ref={rawHtmlIframeRef}
                                        srcDoc={rawHtmlContent}
                                        className="flex-1 w-full border border-gray-200 rounded-md bg-white"
                                        title="HTML Editor"
                                        onLoad={(e) =>
                                            handleIframeLoad(
                                                e.target as HTMLIFrameElement
                                            )
                                        }
                                    />
                                )}
                            </div>
                        ) : (
                            <EditorContent
                                editor={editor}
                                className={`w-full h-full outline-none focus:outline-none
                                    [&_.ProseMirror]:outline-none [&_.ProseMirror]:border-none
                                    [&_.ProseMirror]:min-h-full [&_.ProseMirror]:p-0
                                    [&_.ProseMirror]:text-base
                                    [&_.ProseMirror_pre]:bg-gray-100 [&_.ProseMirror_pre]:rounded-lg [&_.ProseMirror_pre]:p-4 [&_.ProseMirror_pre]:my-2 [&_.ProseMirror_pre]:overflow-x-auto
                                    [&_.ProseMirror_pre_code]:font-mono [&_.ProseMirror_pre_code]:text-sm [&_.ProseMirror_pre_code]:text-gray-800
                                    [&_.ProseMirror_.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]
                                    [&_.ProseMirror_.is-editor-empty:first-child::before]:text-gray-400
                                    [&_.ProseMirror_.is-editor-empty:first-child::before]:pointer-events-none
                                    [&_.ProseMirror_.is-editor-empty:first-child::before]:h-0
                                    [&_.ProseMirror_.is-editor-empty:first-child::before]:float-left
                                    [&_.ProseMirror]:![line-height:var(--editor-line-height,1.5)]`}
                            />
                        )}

                        {/* Inline Variable Suggestion Popup in fullscreen */}
                        {showVariableSuggestion && enableVariables && (
                            <div
                                ref={suggestionRef}
                                className="absolute z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-2 min-w-[280px] max-w-[350px] max-h-[400px] overflow-y-auto"
                                style={{
                                    top: suggestionPosition.top,
                                    left: Math.min(
                                        suggestionPosition.left,
                                        200
                                    ),
                                }}
                            >
                                <div className="px-3 py-1 text-xs text-gray-500 border-b mb-1">
                                    {t('textEditor.selectVariable') ||
                                        'Select variable'}{' '}
                                    <span className="text-gray-400">
                                        (↑↓ Enter)
                                    </span>
                                </div>
                                {filteredSuggestionVariables.length > 0 ? (
                                    filteredSuggestionVariables.map(
                                        (variable, index) => {
                                            const colors = getVariableColor(
                                                variable,
                                                index
                                            )
                                            return (
                                                <button
                                                    key={variable.name}
                                                    onClick={() =>
                                                        handleSelectSuggestion(
                                                            variable.name
                                                        )
                                                    }
                                                    className={`w-full text-left px-3 py-2 flex items-start gap-2.5 transition-colors ${
                                                        index ===
                                                        selectedSuggestionIndex
                                                            ? 'bg-blue-50'
                                                            : 'hover:bg-gray-50'
                                                    }`}
                                                >
                                                    <span
                                                        className={`inline-block w-2.5 h-2.5 rounded-full mt-1 flex-shrink-0 ${colors.dot}`}
                                                    />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-sm font-medium text-gray-800">
                                                            {variable.label}
                                                        </div>
                                                        {variable.description && (
                                                            <div className="text-xs text-gray-500 mt-0.5">
                                                                {
                                                                    variable.description
                                                                }
                                                            </div>
                                                        )}
                                                        <div
                                                            className={`text-xs font-mono mt-0.5 ${colors.text}`}
                                                        >
                                                            ${'{'}$
                                                            {variable.name}
                                                            {'}'}
                                                        </div>
                                                    </div>
                                                </button>
                                            )
                                        }
                                    )
                                ) : (
                                    <div className="px-3 py-2 text-sm text-gray-500">
                                        {t('textEditor.noVariablesFound') ||
                                            'No variables found'}
                                    </div>
                                )}

                                {/* Custom Variable Input */}
                                {allowCustomVariables && (
                                    <div className="px-3 pt-2 mt-1 border-t">
                                        <div className="text-xs text-gray-500 mb-1.5">
                                            {t(
                                                'textEditor.addCustomVariable'
                                            ) || 'Add custom variable'}
                                        </div>
                                        <div className="flex gap-1.5">
                                            <input
                                                ref={customInputRef}
                                                type="text"
                                                value={customVariableName}
                                                onChange={(e) =>
                                                    setCustomVariableName(
                                                        e.target.value
                                                    )
                                                }
                                                onKeyDown={(e) => {
                                                    if (
                                                        e.key === 'Enter' &&
                                                        customVariableName.trim()
                                                    ) {
                                                        e.preventDefault()
                                                        handleAddCustomVariableInline()
                                                    }
                                                    if (e.key === 'Escape') {
                                                        setShowVariableSuggestion(
                                                            false
                                                        )
                                                        setCustomVariableName(
                                                            ''
                                                        )
                                                    }
                                                }}
                                                placeholder={
                                                    t(
                                                        'textEditor.enterVariableName'
                                                    ) || 'Enter name...'
                                                }
                                                className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                            />
                                            <button
                                                onClick={
                                                    handleAddCustomVariableInline
                                                }
                                                disabled={
                                                    !customVariableName.trim()
                                                }
                                                className="px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {t('add') || 'Add'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Preview */}
                    {showPreview && (
                        <div className="w-full md:w-1/2 overflow-auto p-6 bg-gray-50">
                            <div className="text-xs text-gray-500 uppercase tracking-wide mb-3 font-medium border-b pb-2">
                                {t('textEditor.preview')}
                            </div>
                            {rawHtmlMode ? (
                                <iframe
                                    srcDoc={rawHtmlContent}
                                    className="w-full h-full min-h-[400px] border-0 bg-white rounded"
                                    title="HTML Preview"
                                    sandbox="allow-same-origin"
                                />
                            ) : (
                                <div
                                    className="prose prose-sm max-w-none
                                        [&_img]:max-w-full [&_img]:h-auto [&_img]:rounded
                                        [&_a]:text-blue-600 [&_a]:underline
                                        [&_ul]:list-disc [&_ul]:pl-5
                                        [&_ol]:list-decimal [&_ol]:pl-5
                                        [&_p]:my-2
                                        [&_pre]:bg-gray-100 [&_pre]:rounded-lg [&_pre]:p-4 [&_pre]:my-2 [&_pre]:overflow-x-auto
                                        [&_pre_code]:font-mono [&_pre_code]:text-sm [&_pre_code]:text-gray-800"
                                    style={{ lineHeight }}
                                    dangerouslySetInnerHTML={{
                                        __html: getPreviewHtml(
                                            editor?.getHTML() || ''
                                        ),
                                    }}
                                />
                            )}
                        </div>
                    )}
                </div>

                {/* Toolbar */}
                <div className="px-6 py-4 border-t bg-gray-50">
                    <div className="flex items-center justify-between gap-2">
                        {/* Variable hint */}
                        {enableVariables && (
                            <div className="flex items-center gap-2 text-xs text-gray-400">
                                <div className="flex items-center gap-1">
                                    <span className="inline-flex items-center justify-center w-5 h-5 bg-gray-200 rounded text-[10px] font-mono font-medium text-gray-500">
                                        {'{'}
                                    </span>
                                    <span>
                                        {t('textEditor.variableHint1') ||
                                            'chọn biến'}
                                    </span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <span className="inline-flex items-center justify-center px-1 h-5 bg-gray-200 rounded text-[10px] font-mono font-medium text-gray-500">
                                        {'{{'}
                                    </span>
                                    <span>
                                        {t('textEditor.variableHint2') ||
                                            'thêm biến mới'}
                                    </span>
                                </div>
                            </div>
                        )}
                        {!enableVariables && <div />}
                        <div className="flex items-center gap-2">
                            {tools.map((item, index) => {
                                if (item.type === 'select') {
                                    return (
                                        <div key={index} className="relative">
                                            <select
                                                value={item.currentValue}
                                                className="appearance-none bg-white border border-gray-300 rounded px-2 py-1 text-xs font-medium text-gray-600 hover:border-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20 cursor-pointer"
                                            >
                                                {item.options?.map((option) => (
                                                    <option
                                                        key={option.value}
                                                        value={option.value}
                                                    >
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )
                                }
                                return (
                                    <Tooltip
                                        key={index}
                                        title={item.title || ''}
                                    >
                                        <div
                                            className={`flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-gray-200 ${
                                                item?.active
                                                    ? 'text-blue-600 bg-blue-100'
                                                    : 'text-gray-600'
                                            }`}
                                            onClick={(e) => {
                                                e.preventDefault()
                                                item.action()
                                            }}
                                        >
                                            {item.icon}
                                        </div>
                                    </Tooltip>
                                )
                            })}
                        </div>
                    </div>
                </div>

                {/* Attachments in fullscreen mode */}
                {attachments?.length > 0 && (
                    <div className="px-6 pb-4 bg-gray-50 max-h-40 overflow-auto">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {attachments.map((item, index) => (
                                <div
                                    key={index}
                                    className="flex items-center justify-between p-3 bg-white rounded border"
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm truncate font-medium">
                                                {item.file.name}
                                            </span>
                                            {item.uploading && (
                                                <span className="text-xs text-blue-600 font-medium">
                                                    Đang tải...
                                                </span>
                                            )}
                                            {item.url && !item.uploading && (
                                                <span className="text-xs text-green-600 font-medium">
                                                    ✓ Hoàn thành
                                                </span>
                                            )}
                                        </div>
                                        {item.uploading && (
                                            <div className="mt-1">
                                                <div className="w-full bg-gray-200 rounded-full h-1.5">
                                                    <div
                                                        className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                                                        style={{
                                                            width: `${item.progress || 0}%`,
                                                        }}
                                                    ></div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {!item.uploading && (
                                        <button
                                            onClick={() =>
                                                setAttachments(
                                                    attachments.filter(
                                                        (_, i) => i !== index
                                                    )
                                                )
                                            }
                                            className="ml-2 text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50 transition-colors"
                                        >
                                            <svg
                                                className="w-4 h-4"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Link Input Popup in fullscreen */}
                {showLinkInput && (
                    <div
                        ref={linkPopupRef}
                        className="absolute bottom-24 right-6 z-50"
                    >
                        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-2 flex items-center gap-2">
                            <input
                                ref={linkInputRef}
                                type="text"
                                value={linkUrl}
                                onChange={(e) => setLinkUrl(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault()
                                        handleApplyLink()
                                    }
                                    if (e.key === 'Escape') {
                                        setShowLinkInput(false)
                                        setLinkUrl('')
                                    }
                                }}
                                placeholder={t('paste_link')}
                                className="w-64 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <button
                                onClick={handleApplyLink}
                                className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            >
                                <svg
                                    className="w-4 h-4"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M5 13l4 4L19 7"
                                    />
                                </svg>
                            </button>
                            {editor?.isActive('link') && (
                                <button
                                    onClick={handleRemoveLink}
                                    className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                >
                                    <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                        />
                                    </svg>
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* Import HTML Popup in fullscreen */}
                {showImportHtml && (
                    <div
                        ref={importHtmlRef}
                        className="absolute bottom-24 right-6 z-50"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 w-96">
                            <div className="flex items-center justify-between mb-3">
                                <span className="text-sm font-medium text-gray-700">
                                    {t('textEditor.importHtml') ||
                                        'Import HTML'}
                                </span>
                                <button
                                    onClick={() => {
                                        setShowImportHtml(false)
                                        setImportHtmlValue('')
                                        setImportHtmlError('')
                                    }}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                </button>
                            </div>
                            <textarea
                                value={importHtmlValue}
                                onChange={(e) => {
                                    setImportHtmlValue(e.target.value)
                                    setImportHtmlError('')
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                        setShowImportHtml(false)
                                        setImportHtmlValue('')
                                        setImportHtmlError('')
                                    }
                                }}
                                placeholder={
                                    t('textEditor.pasteHtml') ||
                                    'Paste HTML code here...'
                                }
                                className="w-full h-32 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono resize-none"
                            />
                            {importHtmlError && (
                                <div className="mt-2 text-xs text-red-500 flex items-center gap-1">
                                    <svg
                                        className="w-3 h-3"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                    {importHtmlError}
                                </div>
                            )}
                            {/* Preserve Layout Option */}
                            <div className="mt-2 flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="preserve-layout-fs"
                                    checked={preserveLayout}
                                    onChange={(e) =>
                                        setPreserveLayout(e.target.checked)
                                    }
                                    className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <label
                                    htmlFor="preserve-layout-fs"
                                    className="text-xs text-gray-600"
                                >
                                    {t('textEditor.preserveLayout') ||
                                        'Preserve layout (for email templates)'}
                                </label>
                            </div>
                            <div className="mt-3 flex items-center justify-between">
                                <button
                                    onClick={handleImportHtmlFile}
                                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                >
                                    <svg
                                        className="w-3 h-3"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                        />
                                    </svg>
                                    {t('textEditor.uploadFile') ||
                                        'Upload file'}
                                </button>
                                <button
                                    onClick={handleImportHtml}
                                    disabled={!importHtmlValue.trim()}
                                    className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {t('textEditor.import') || 'Import'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Variable Popup in fullscreen */}
                {showVariablePopup && enableVariables && (
                    <div
                        ref={variablePopupRef}
                        className="absolute bottom-24 right-6 z-50 variable-popup"
                        data-editor-action="variable"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 w-80">
                            <div className="flex items-center justify-between mb-3">
                                <span className="text-sm font-medium text-gray-700">
                                    {t('textEditor.insertVariable') ||
                                        'Insert Variable'}
                                </span>
                                <button
                                    onClick={() => {
                                        setShowVariablePopup(false)
                                        setCustomVariableName('')
                                    }}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                </button>
                            </div>

                            {/* Predefined Variables */}
                            <div className="mb-3">
                                <div className="text-xs text-gray-500 mb-2">
                                    {t('textEditor.availableVariables') ||
                                        'Available Variables'}
                                </div>
                                <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                                    {allVariables.map((variable, index) => {
                                        const colors = getVariableColor(
                                            variable,
                                            index
                                        )
                                        return (
                                            <button
                                                key={variable.name}
                                                onClick={() =>
                                                    handleInsertVariable(
                                                        variable.name
                                                    )
                                                }
                                                className={`inline-flex flex-col items-start px-2.5 py-1.5 text-xs rounded-md transition-colors border ${colors.bg} ${colors.text} ${colors.border} ${colors.hover}`}
                                                title={variable.description}
                                            >
                                                <span className="font-medium">
                                                    {variable.label}
                                                </span>
                                                <span className="font-mono text-[10px] opacity-70">
                                                    ${'{'}${variable.name}
                                                    {'}'}
                                                </span>
                                            </button>
                                        )
                                    })}
                                </div>
                            </div>

                            {/* Custom Variable Input */}
                            {allowCustomVariables && (
                                <div className="pt-3 border-t">
                                    <div className="text-xs text-gray-500 mb-2">
                                        {t('textEditor.addCustomVariable')}
                                    </div>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={customVariableName}
                                            onChange={(e) =>
                                                setCustomVariableName(
                                                    e.target.value
                                                )
                                            }
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault()
                                                    handleAddCustomVariable()
                                                }
                                                if (e.key === 'Escape') {
                                                    setShowVariablePopup(false)
                                                    setCustomVariableName('')
                                                }
                                            }}
                                            placeholder={
                                                t('textEditor.variableName') ||
                                                'Variable name...'
                                            }
                                            className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        />
                                        <button
                                            onClick={handleAddCustomVariable}
                                            disabled={
                                                !customVariableName.trim()
                                            }
                                            className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            {t('add') || 'Add'}
                                        </button>
                                    </div>
                                    <div className="mt-2 text-xs text-gray-400">
                                        {t('textEditor.variableHint')}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        )
    }

    return (
        <div className={`relative ${className}`}>
            {/* MUI TextField-like label */}
            {label && (
                <label
                    className={`absolute left-3 transition-all duration-200 pointer-events-none z-10 ${
                        shouldShrinkLabel
                            ? 'top-0 text-xs text-primary bg-white px-1 -translate-y-1/2'
                            : 'top-4 text-sm text-gray-500'
                    }`}
                >
                    {label}
                </label>
            )}

            <div
                className={`${label ? 'mt-2' : ''} p-0 ${readOnly ? '' : 'border border-gray-300'} rounded-[4px] cursor-text flex-1 flex flex-col w-full
                     transition-all duration-200 outline-none`}
                onClick={() => !readOnly && editor.commands.focus()}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
            >
                {shouldTruncate ? (
                    <div
                        style={{
                            ['--editor-line-height' as string]: lineHeight,
                        }}
                    >
                        <EditorContent
                            editor={editor}
                            className={`px-0 text-sm flex-1 w-full outline-none focus:outline-none
                           [&_.ProseMirror]:outline-none [&_.ProseMirror]:border-none
                           [&_.ProseMirror]:![line-height:var(--editor-line-height,1.5)]
                          [&_.ProseMirror]:rounded
                           [&_.ProseMirror.drag-cursor]:cursor-crosshair
                           [&_.ProseMirror]:border-2 [&_.ProseMirror]:border-transparent
                           [&_.ProseMirror.dragover]:border-primary [&_.ProseMirror.dragover]:bg-blue-50/30
                           [&_.ProseMirror]:text-sm
                           [&_.ProseMirror_pre]:bg-gray-100 [&_.ProseMirror_pre]:rounded-lg [&_.ProseMirror_pre]:p-4 [&_.ProseMirror_pre]:my-2 [&_.ProseMirror_pre]:overflow-x-auto
                           [&_.ProseMirror_pre_code]:font-mono [&_.ProseMirror_pre_code]:text-sm [&_.ProseMirror_pre_code]:text-gray-800
                           ${
                               shouldShrinkLabel
                                   ? `[&_.ProseMirror_.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]
                              [&_.ProseMirror_.is-editor-empty:first-child::before]:text-gray-400
                              [&_.ProseMirror_.is-editor-empty:first-child::before]:pointer-events-none
                              [&_.ProseMirror_.is-editor-empty:first-child::before]:h-0
                              [&_.ProseMirror_.is-editor-empty:first-child::before]:float-left`
                                   : `[&_.ProseMirror_.is-editor-empty:first-child::before]:content-none`
                           }
                           ${readOnly ? `[&_.ProseMirror]:min-h-0 [&_.ProseMirror]:p-0` : `[&_.ProseMirror]:min-h-[120px] [&_.ProseMirror]:p-4 [&_.ProseMirror]:text-gray-700`} ${className}`}
                            style={{
                                display: '-webkit-box',
                                WebkitLineClamp: maxLine,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }}
                        />
                    </div>
                ) : (
                    <div
                        ref={contentRef}
                        style={{
                            ['--editor-line-height' as string]: lineHeight,
                        }}
                        className="relative"
                    >
                        {rawHtmlMode ? (
                            /* Raw HTML Mode - editable iframe */
                            <div
                                className="w-full min-h-[120px] flex flex-col"
                                data-editor-action="raw-html-container"
                            >
                                <div className="flex items-center justify-between mb-2 px-4 pt-4">
                                    <span className="text-sm font-medium text-gray-700">
                                        {t('textEditor.htmlPreview') ||
                                            'HTML Preview'}
                                    </span>
                                    <div className="flex gap-2">
                                        <button
                                            data-editor-action="toggle-source"
                                            onClick={() =>
                                                showRawHtmlEditor
                                                    ? setShowRawHtmlEditor(
                                                          false
                                                      )
                                                    : handleShowSourceEditor()
                                            }
                                            className="text-xs text-blue-600 hover:text-blue-800"
                                        >
                                            {showRawHtmlEditor
                                                ? t('textEditor.showPreview') ||
                                                  'Show Preview'
                                                : t('textEditor.editSource') ||
                                                  'Edit Source'}
                                        </button>
                                        <button
                                            onClick={() => {
                                                setRawHtmlMode(false)
                                                setRawHtmlContent('')
                                                rawHtmlContentRef.current = ''
                                                setShowRawHtmlEditor(false)
                                                editor
                                                    ?.chain()
                                                    .focus()
                                                    .setContent('')
                                                    .run()
                                            }}
                                            className="text-xs text-red-600 hover:text-red-800"
                                        >
                                            {t('textEditor.exitRawMode') ||
                                                'Exit Raw Mode'}
                                        </button>
                                    </div>
                                </div>
                                {showRawHtmlEditor ? (
                                    <textarea
                                        value={rawHtmlContent}
                                        onChange={(e) => {
                                            setRawHtmlContent(e.target.value)
                                            rawHtmlContentRef.current =
                                                e.target.value
                                            onChange?.({
                                                text: e.target.value,
                                                attachments,
                                            })
                                        }}
                                        className="min-h-[200px] w-full p-4 text-sm border-0 focus:outline-none focus:ring-0 font-mono resize-none"
                                    />
                                ) : (
                                    <iframe
                                        ref={rawHtmlIframeRef}
                                        srcDoc={rawHtmlContent}
                                        className="w-full border-0 bg-white"
                                        style={{ minHeight: '200px' }}
                                        title="HTML Editor"
                                        onLoad={(e) =>
                                            handleIframeLoad(
                                                e.target as HTMLIFrameElement
                                            )
                                        }
                                    />
                                )}
                            </div>
                        ) : (
                            <EditorContent
                                editor={editor}
                                className={`flex-1 w-full outline-none focus:outline-none
                           [&_.ProseMirror]:outline-none [&_.ProseMirror]:border-none
                           [&_.ProseMirror]:![line-height:var(--editor-line-height,1.5)]
                          [&_.ProseMirror]:rounded
                           [&_.ProseMirror.drag-cursor]:cursor-crosshair
                           [&_.ProseMirror]:border-2 [&_.ProseMirror]:border-transparent
                           [&_.ProseMirror.dragover]:border-primary [&_.ProseMirror.dragover]:bg-blue-50/30
                           [&_.ProseMirror]:text-sm
                           [&_.ProseMirror_pre]:bg-gray-100 [&_.ProseMirror_pre]:rounded-lg [&_.ProseMirror_pre]:p-4 [&_.ProseMirror_pre]:my-2 [&_.ProseMirror_pre]:overflow-x-auto
                           [&_.ProseMirror_pre_code]:font-mono [&_.ProseMirror_pre_code]:text-sm [&_.ProseMirror_pre_code]:text-gray-800
                           ${
                               shouldShrinkLabel
                                   ? `[&_.ProseMirror_.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]
                              [&_.ProseMirror_.is-editor-empty:first-child::before]:text-gray-400
                              [&_.ProseMirror_.is-editor-empty:first-child::before]:pointer-events-none
                              [&_.ProseMirror_.is-editor-empty:first-child::before]:h-0
                              [&_.ProseMirror_.is-editor-empty:first-child::before]:float-left`
                                   : `[&_.ProseMirror_.is-editor-empty:first-child::before]:content-none`
                           }
                           ${readOnly ? `[&_.ProseMirror]:min-h-0 [&_.ProseMirror]:p-0` : `[&_.ProseMirror]:min-h-[120px] [&_.ProseMirror]:p-4 [&_.ProseMirror]:text-gray-700`} ${className}`}
                            />
                        )}

                        {/* Inline Variable Suggestion Popup */}
                        {showVariableSuggestion && enableVariables && (
                            <div
                                ref={suggestionRef}
                                className="absolute z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-2 min-w-[280px] max-w-[350px] max-h-[400px] overflow-y-auto"
                                style={{
                                    top: suggestionPosition.top,
                                    left: Math.min(
                                        suggestionPosition.left,
                                        200
                                    ),
                                }}
                            >
                                <div className="px-3 py-1 text-xs text-gray-500 border-b mb-1">
                                    {t('textEditor.selectVariable') ||
                                        'Select variable'}{' '}
                                    <span className="text-gray-400">
                                        (↑↓ Enter)
                                    </span>
                                </div>
                                {filteredSuggestionVariables.length > 0 ? (
                                    filteredSuggestionVariables.map(
                                        (variable, index) => {
                                            const colors = getVariableColor(
                                                variable,
                                                index
                                            )
                                            return (
                                                <button
                                                    key={variable.name}
                                                    onClick={() =>
                                                        handleSelectSuggestion(
                                                            variable.name
                                                        )
                                                    }
                                                    className={`w-full text-left px-3 py-2 flex items-start gap-2.5 transition-colors ${
                                                        index ===
                                                        selectedSuggestionIndex
                                                            ? 'bg-blue-50'
                                                            : 'hover:bg-gray-50'
                                                    }`}
                                                >
                                                    <span
                                                        className={`inline-block w-2.5 h-2.5 rounded-full mt-1 flex-shrink-0 ${colors.dot}`}
                                                    />
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-sm font-medium text-gray-800">
                                                            {variable.label}
                                                        </div>
                                                        {variable.description && (
                                                            <div className="text-xs text-gray-500 mt-0.5">
                                                                {
                                                                    variable.description
                                                                }
                                                            </div>
                                                        )}
                                                        <div
                                                            className={`text-xs font-mono mt-0.5 ${colors.text}`}
                                                        >
                                                            ${'{'}$
                                                            {variable.name}
                                                            {'}'}
                                                        </div>
                                                    </div>
                                                </button>
                                            )
                                        }
                                    )
                                ) : (
                                    <div className="px-3 py-2 text-sm text-gray-500">
                                        {t('textEditor.noVariablesFound') ||
                                            'No variables found'}
                                    </div>
                                )}

                                {/* Custom Variable Input */}
                                {allowCustomVariables && (
                                    <div className="px-3 pt-2 mt-1 border-t">
                                        <div className="text-xs text-gray-500 mb-1.5">
                                            {t(
                                                'textEditor.addCustomVariable'
                                            ) || 'Add custom variable'}
                                        </div>
                                        <div className="flex gap-1.5">
                                            <input
                                                ref={customInputRef}
                                                type="text"
                                                value={customVariableName}
                                                onChange={(e) =>
                                                    setCustomVariableName(
                                                        e.target.value
                                                    )
                                                }
                                                onKeyDown={(e) => {
                                                    if (
                                                        e.key === 'Enter' &&
                                                        customVariableName.trim()
                                                    ) {
                                                        e.preventDefault()
                                                        handleAddCustomVariableInline()
                                                    }
                                                    if (e.key === 'Escape') {
                                                        setShowVariableSuggestion(
                                                            false
                                                        )
                                                        setCustomVariableName(
                                                            ''
                                                        )
                                                    }
                                                }}
                                                placeholder={
                                                    t(
                                                        'textEditor.enterVariableName'
                                                    ) || 'Enter name...'
                                                }
                                                className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                            />
                                            <button
                                                onClick={
                                                    handleAddCustomVariableInline
                                                }
                                                disabled={
                                                    !customVariableName.trim()
                                                }
                                                className="px-2 py-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {t('add') || 'Add'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Show more/less button for readOnly mode - only when content is actually truncated */}
                {readOnly && maxLine && isContentTruncated && !isExpanded && (
                    <div className="flex mt-0 ms-2">
                        <Typogaphy
                            onClick={() => setIsExpanded(!isExpanded)}
                            className="text-primary hover:text-primary/80 text-xs underline transition-colors cursor-pointer"
                        >
                            {t('viewMore')}
                        </Typogaphy>
                    </div>
                )}

                {/* Display images from HTML content in readOnly mode */}
                {readOnly &&
                    value?.text &&
                    extractImagesFromHTML(value.text).length > 0 && (
                        <div className="mt-4">
                            {extractImagesFromHTML(value.text).length === 1 ? (
                                /* Single image - full width */
                                <div
                                    className="relative cursor-pointer group rounded-lg overflow-hidden border border-gray-200 hover:border-gray-300 transition-colors"
                                    onClick={() =>
                                        handlePreviewFile(
                                            extractImagesFromHTML(value.text)[0]
                                                .src,
                                            extractImagesFromHTML(value.text)[0]
                                                .alt
                                        )
                                    }
                                >
                                    <img
                                        src={
                                            extractImagesFromHTML(value.text)[0]
                                                .src
                                        }
                                        alt={
                                            extractImagesFromHTML(value.text)[0]
                                                .alt
                                        }
                                        className="w-full h-auto max-h-80 object-cover group-hover:scale-[1.02] transition-transform duration-200 rounded-lg"
                                    />
                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 flex items-center justify-center">
                                        <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                            <svg
                                                className="w-8 h-8 text-white"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M3 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 13a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1v-3zM12 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1V4zM12 13a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-3z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                /* Multiple images - grid layout */
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                                    {extractImagesFromHTML(value.text).map(
                                        (image, index) => (
                                            <div
                                                key={index}
                                                className="relative cursor-pointer group rounded-lg overflow-hidden border border-gray-200 hover:border-gray-300 transition-colors"
                                                onClick={() =>
                                                    handlePreviewFile(
                                                        image.src,
                                                        image.alt
                                                    )
                                                }
                                            >
                                                <img
                                                    src={image.src}
                                                    alt={image.alt}
                                                    className="w-full h-20 object-cover group-hover:scale-105 transition-transform duration-200"
                                                />
                                                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200 flex items-center justify-center">
                                                    <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <svg
                                                            className="w-6 h-6 text-white"
                                                            fill="currentColor"
                                                            viewBox="0 0 20 20"
                                                        >
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M3 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 13a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1v-3zM12 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1V4zM12 13a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-3z"
                                                                clipRule="evenodd"
                                                            />
                                                        </svg>
                                                    </div>
                                                </div>
                                            </div>
                                        )
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                {/* Display attachments in readOnly mode */}
                {readOnly &&
                    attachments?.filter((item) => item.url).length > 0 && (
                        <div className="mt-4">
                            <Typogaphy className="text-sm font-medium text-gray-700 mb-2">
                                File đính kèm
                            </Typogaphy>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                {attachments
                                    .filter((item) => item.url)
                                    .map((item, index) => {
                                        const IconComponent = getFileIcon(
                                            item.file.name
                                        )
                                        return (
                                            <div
                                                key={index}
                                                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border hover:border-gray-300 cursor-pointer transition-colors group"
                                                onClick={() =>
                                                    handlePreviewFile(
                                                        item.url!,
                                                        item.file.name
                                                    )
                                                }
                                            >
                                                <div className="w-8 h-8 flex-shrink-0">
                                                    <IconComponent />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-medium text-gray-900 truncate group-hover:text-primary transition-colors">
                                                        {item.file.name}
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        {(
                                                            item.file.size /
                                                            1024 /
                                                            1024
                                                        ).toFixed(2)}{' '}
                                                        MB
                                                    </div>
                                                </div>
                                                <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <svg
                                                        className="w-4 h-4 text-gray-400"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M3 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 13a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1v-3zM12 4a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1V4zM12 13a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-3z"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                </div>
                                            </div>
                                        )
                                    })}
                            </div>
                        </div>
                    )}

                {!disabled && !readOnly && (
                    <div className="relative">
                        {/* Link Input Popup */}
                        {showLinkInput && (
                            <div
                                ref={linkPopupRef}
                                className="absolute bottom-full right-4 mb-2 z-50"
                            >
                                <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-2 flex items-center gap-2">
                                    <input
                                        ref={linkInputRef}
                                        type="text"
                                        value={linkUrl}
                                        onChange={(e) =>
                                            setLinkUrl(e.target.value)
                                        }
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault()
                                                handleApplyLink()
                                            }
                                            if (e.key === 'Escape') {
                                                setShowLinkInput(false)
                                                setLinkUrl('')
                                            }
                                        }}
                                        placeholder={t('paste_link')}
                                        className="w-64 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                    <button
                                        onClick={handleApplyLink}
                                        className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                        title={t('apply')}
                                    >
                                        <svg
                                            className="w-4 h-4"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M5 13l4 4L19 7"
                                            />
                                        </svg>
                                    </button>
                                    {linkUrl && (
                                        <button
                                            onClick={handleOpenLink}
                                            className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                            title={t('open_link')}
                                        >
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                                />
                                            </svg>
                                        </button>
                                    )}
                                    {editor?.isActive('link') && (
                                        <button
                                            onClick={handleRemoveLink}
                                            className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                            title={t('remove_link')}
                                        >
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Import HTML Popup */}
                        {showImportHtml && (
                            <div
                                ref={importHtmlRef}
                                className="absolute bottom-full right-4 mb-2 z-50"
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                            >
                                <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 w-96">
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="text-sm font-medium text-gray-700">
                                            {t('textEditor.importHtml') ||
                                                'Import HTML'}
                                        </span>
                                        <button
                                            onClick={() => {
                                                setShowImportHtml(false)
                                                setImportHtmlValue('')
                                                setImportHtmlError('')
                                            }}
                                            className="text-gray-400 hover:text-gray-600"
                                        >
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M6 18L18 6M6 6l12 12"
                                                />
                                            </svg>
                                        </button>
                                    </div>
                                    <textarea
                                        value={importHtmlValue}
                                        onChange={(e) => {
                                            setImportHtmlValue(e.target.value)
                                            setImportHtmlError('')
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Escape') {
                                                setShowImportHtml(false)
                                                setImportHtmlValue('')
                                                setImportHtmlError('')
                                            }
                                        }}
                                        placeholder={
                                            t('textEditor.pasteHtml') ||
                                            'Paste HTML code here...'
                                        }
                                        className="w-full h-32 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono resize-none"
                                    />
                                    {importHtmlError && (
                                        <div className="mt-2 text-xs text-red-500 flex items-center gap-1">
                                            <svg
                                                className="w-3 h-3"
                                                fill="currentColor"
                                                viewBox="0 0 20 20"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                            {importHtmlError}
                                        </div>
                                    )}
                                    {/* Preserve Layout Option */}
                                    <div className="mt-2 flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            id="preserve-layout"
                                            checked={preserveLayout}
                                            onChange={(e) =>
                                                setPreserveLayout(
                                                    e.target.checked
                                                )
                                            }
                                            className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <label
                                            htmlFor="preserve-layout"
                                            className="text-xs text-gray-600"
                                        >
                                            {t('textEditor.preserveLayout') ||
                                                'Preserve layout (for email templates)'}
                                        </label>
                                    </div>
                                    <div className="mt-3 flex items-center justify-between">
                                        <button
                                            onClick={handleImportHtmlFile}
                                            className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                        >
                                            <svg
                                                className="w-3 h-3"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                                />
                                            </svg>
                                            {t('textEditor.uploadFile') ||
                                                'Upload file'}
                                        </button>
                                        <button
                                            onClick={handleImportHtml}
                                            disabled={!importHtmlValue.trim()}
                                            className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            {t('textEditor.import') || 'Import'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Variable Popup */}
                        {showVariablePopup && enableVariables && (
                            <div
                                ref={variablePopupRef}
                                className="absolute bottom-full right-4 mb-2 z-50 variable-popup"
                                data-editor-action="variable"
                                onClick={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                            >
                                <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 w-80">
                                    <div className="flex items-center justify-between mb-3">
                                        <span className="text-sm font-medium text-gray-700">
                                            {t('textEditor.insertVariable') ||
                                                'Insert Variable'}
                                        </span>
                                        <button
                                            onClick={() => {
                                                setShowVariablePopup(false)
                                                setCustomVariableName('')
                                            }}
                                            className="text-gray-400 hover:text-gray-600"
                                        >
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M6 18L18 6M6 6l12 12"
                                                />
                                            </svg>
                                        </button>
                                    </div>

                                    {/* Predefined Variables */}
                                    <div className="mb-3">
                                        <div className="text-xs text-gray-500 mb-2">
                                            {t(
                                                'textEditor.availableVariables'
                                            ) || 'Available Variables'}
                                        </div>
                                        <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                                            {allVariables.map(
                                                (variable, index) => {
                                                    const colors =
                                                        getVariableColor(
                                                            variable,
                                                            index
                                                        )
                                                    return (
                                                        <button
                                                            key={variable.name}
                                                            onClick={() =>
                                                                handleInsertVariable(
                                                                    variable.name
                                                                )
                                                            }
                                                            className={`inline-flex flex-col items-start px-2.5 py-1.5 text-xs rounded-md transition-colors border ${colors.bg} ${colors.text} ${colors.border} ${colors.hover}`}
                                                            title={
                                                                variable.description
                                                            }
                                                        >
                                                            <span className="font-medium">
                                                                {variable.label}
                                                            </span>
                                                            <span className="font-mono text-[10px] opacity-70">
                                                                ${'{'}$
                                                                {variable.name}
                                                                {'}'}
                                                            </span>
                                                        </button>
                                                    )
                                                }
                                            )}
                                        </div>
                                    </div>

                                    {/* Custom Variable Input */}
                                    {allowCustomVariables && (
                                        <div className="pt-3 border-t">
                                            <div className="text-xs text-gray-500 mb-2">
                                                {t(
                                                    'textEditor.addCustomVariable'
                                                ) || 'Add Custom Variable'}
                                            </div>
                                            <div className="flex gap-2">
                                                <input
                                                    type="text"
                                                    value={customVariableName}
                                                    onChange={(e) =>
                                                        setCustomVariableName(
                                                            e.target.value
                                                        )
                                                    }
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                            e.preventDefault()
                                                            handleAddCustomVariable()
                                                        }
                                                        if (
                                                            e.key === 'Escape'
                                                        ) {
                                                            setShowVariablePopup(
                                                                false
                                                            )
                                                            setCustomVariableName(
                                                                ''
                                                            )
                                                        }
                                                    }}
                                                    placeholder={
                                                        t(
                                                            'textEditor.variableName'
                                                        ) || 'Variable name...'
                                                    }
                                                    className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                />
                                                <button
                                                    onClick={
                                                        handleAddCustomVariable
                                                    }
                                                    disabled={
                                                        !customVariableName.trim()
                                                    }
                                                    className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                >
                                                    {t('add') || 'Add'}
                                                </button>
                                            </div>
                                            <div className="mt-2 text-xs text-gray-400">
                                                {t('textEditor.variableHint') ||
                                                    'Will be inserted as ${variable_name}'}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="flex items-center justify-between mt-4 gap-2 px-4 pb-2">
                            {/* Variable hint */}
                            {enableVariables && (
                                <div className="flex items-center gap-2 text-xs text-gray-400">
                                    <div className="flex items-center gap-1">
                                        <span className="inline-flex items-center justify-center w-5 h-5 bg-gray-100 rounded text-[10px] font-mono font-medium text-gray-500">
                                            {'{'}
                                        </span>
                                        <span>
                                            {t('textEditor.variableHint1') ||
                                                'chọn biến'}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <span className="inline-flex items-center justify-center px-1 h-5 bg-gray-100 rounded text-[10px] font-mono font-medium text-gray-500">
                                            {'{{'}
                                        </span>
                                        <span>
                                            {t('textEditor.variableHint2') ||
                                                'thêm biến mới'}
                                        </span>
                                    </div>
                                </div>
                            )}
                            {!enableVariables && <div />}
                            <div className="flex items-center gap-2">
                                {tools.map((item, index) => {
                                    if (item.type === 'select') {
                                        return (
                                            <div
                                                key={index}
                                                className="relative"
                                            >
                                                <select
                                                    value={item.currentValue}
                                                    className="appearance-none bg-white border border-gray-300 rounded px-2 py-1 text-xs font-medium text-gray-600 hover:border-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20 cursor-pointer"
                                                >
                                                    {item.options?.map(
                                                        (option) => (
                                                            <option
                                                                key={
                                                                    option.value
                                                                }
                                                                value={
                                                                    option.value
                                                                }
                                                            >
                                                                {option.label}
                                                            </option>
                                                        )
                                                    )}
                                                </select>
                                                <div className="absolute right-1 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                                    <svg
                                                        className="w-3 h-3 text-gray-400"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={1}
                                                            d="M19 9l-7 7-7-7"
                                                        />
                                                    </svg>
                                                </div>
                                            </div>
                                        )
                                    }
                                    return (
                                        <Tooltip
                                            key={index}
                                            title={item.title || ''}
                                        >
                                            <div
                                                className={`flex items-center gap-2 cursor-pointer p-1 rounded hover:bg-gray-100 ${
                                                    item?.active
                                                        ? 'text-blue-600 bg-blue-50'
                                                        : 'text-gray-600'
                                                }`}
                                                onClick={(e) => {
                                                    e.preventDefault()
                                                    item.action()
                                                }}
                                            >
                                                {item.icon}
                                            </div>
                                        </Tooltip>
                                    )
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {/* Attachments in edit mode */}
                {!readOnly && attachments?.length > 0 && (
                    <div className="grid grid-cols-1 gap-2 mt-2">
                        {attachments.map((item, index) => (
                            <div
                                key={index}
                                className="flex items-center justify-between p-3 bg-gray-50 rounded border"
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm truncate font-medium">
                                            {item.file.name}
                                        </span>
                                        {item.uploading && (
                                            <span className="text-xs text-blue-600 font-medium">
                                                Đang tải...
                                            </span>
                                        )}
                                        {item.url && !item.uploading && (
                                            <span className="text-xs text-green-600 font-medium">
                                                ✓ Hoàn thành
                                            </span>
                                        )}
                                    </div>

                                    {/* Progress bar */}
                                    {item.uploading && (
                                        <div className="mt-1">
                                            <div className="w-full bg-gray-200 rounded-full h-1.5">
                                                <div
                                                    className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                                                    style={{
                                                        width: `${item.progress || 0}%`,
                                                    }}
                                                ></div>
                                            </div>
                                            <span className="text-xs text-gray-500 mt-1">
                                                {item.progress || 0}%
                                            </span>
                                        </div>
                                    )}

                                    {/* File info */}
                                    <div className="text-xs text-gray-500 mt-1">
                                        {(item.file.size / 1024 / 1024).toFixed(
                                            2
                                        )}{' '}
                                        MB
                                        {item.url && (
                                            <span className="ml-2">
                                                •{' '}
                                                <a
                                                    href={item.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-600 hover:underline"
                                                >
                                                    Xem file
                                                </a>
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="ml-2 flex items-center gap-1">
                                    {!item.uploading && (
                                        <Tooltip
                                            title={t(
                                                'textEditor.deleteAttachment'
                                            )}
                                        >
                                            <button
                                                onClick={() =>
                                                    setAttachments(
                                                        attachments.filter(
                                                            (_, i) =>
                                                                i !== index
                                                        )
                                                    )
                                                }
                                                className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50 transition-colors"
                                            >
                                                <svg
                                                    className="w-4 h-4"
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                                        clipRule="evenodd"
                                                    />
                                                </svg>
                                            </button>
                                        </Tooltip>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}

export default memo(TextEditorCustomize)
export type { TemplateVariable }
export { DEFAULT_TEMPLATE_VARIABLES }
